/**
 * fx.js
 * Transient combat/board feedback layer.
 *
 * TRANSIENT effects — numbers, rings, ground kicks, travelling clones — live
 * in a persistent #fx-layer overlay, because the battle board is torn down and
 * rebuilt on every state update. The layer and everything in it is
 * `pointer-events: none !important` (main.css declares `* { pointer-events:
 * auto }`, which beats inheritance, so naming the descendants is the only way).
 *
 * A board card's own MOTION is the deliberate exception: landings, strikes and
 * knock-backs move the REAL card by its slot mover (`moveCard`), so the card
 * is never hidden behind a stand-in and never leaves hit testing. That costs a
 * disposable node, so `moveCard` resumes its animation on the replacement when
 * a snapshot rebuilds the lane.
 *
 * Motion uses the Web Animations API when available — every call gets an
 * independent Animation, so rapid successive effects never truncate each
 * other — with a CSS-class fallback elsewhere.
 */


import { Board } from './board.js';
import { Cards } from './cards.js';
import { Effects } from './effects.js';
import { SoundEffects } from './sound-effects.js';
import { cleanStatusToken, splitStatusSegments } from './status-segments.js';
import { StoryFeed } from './story-feed.js';
import { ThinkingIndicator } from './thinking-indicator.js';
import { uiPx } from './ui-scale.js';
let FX = {};

FX = (function () {
    const LAYER_ID = 'fx-layer';
    const NUMBER_LAYER_ID = 'fx-number-layer';
    // A single death legitimately holds five nodes and a landing four, so the
    // ceiling has to clear two overlapping board events without either one
    // erasing the other.
    const MAX_ACTIVE = 26;
    // Per-type caps keep the loudest, most repeatable effects from starving
    // the rarer ones: a single landing legitimately spawns six nodes, so the
    // global cap alone would let two simultaneous summons evict each other.
    const TYPE_CAPS = {
        'fx-number': 6,
        'fx-ghost': 5,
        'fx-glide': 3,
        'fx-impact-ring': 6,
        'fx-debris': 8,
        'fx-shockwave': 4,
        'fx-landing-halo': 3,
        'fx-death-shadow': 5,
        'fx-crack': 6,
        'fx-death-piece': 8,
        'fx-flash': 4,
        'fx-touch-gather': 4,
        'fx-touch-sweep': 4,
        'fx-rise': 6,
    };
    const STALE_MS = 5000;
    // Held glides (including an enemy summon completing its board handoff) may
    // legally outlive the normal transient sweep for a short bounded window.
    const PERSIST_STALE_MS = 60000;
    const POP_EASING = 'cubic-bezier(0.2, 0.9, 0.3, 1)';
    // A body under gravity: released gently, arriving hard.
    const FALL_EASING = 'cubic-bezier(0.55, 0, 0.85, 0.35)';
    // A coiled strike leaves its wind-up violently.
    const STRIKE_EASING = 'cubic-bezier(0.72, 0, 0.86, 0.12)';
    // Recovery with a little life left in it.
    const SETTLE_EASING = 'cubic-bezier(0.22, 1.2, 0.36, 1)';

    // The landing, the strike and the exit: the three beats the board is felt
    // through. Every one of them moves the REAL card (see `moveCard`), so none
    // of them may hide, cover or otherwise withhold it.
    const SUMMON_LAND_MS = 620;
    /* The summon cue is a swell whose loudest instant sits this far into the
       clip (measured on the trimmed asset; it was 543ms before the 200ms of
       dead air was cut). A landing starts the clip that far AHEAD of its own
       contact so the peak lands on the contact frame — the accept event that
       used to own the cue precedes contact by an amount nobody can know. */
    const SUMMON_CUE_PEAK_MS = 373;
    const ATTACK_LUNGE_MS = 520;
    // A death is two beats: the body takes the blow while the fracture opens,
    // then it breaks along that fracture and the pieces carry the fade.
    const DEATH_BODY_MS = 260;
    const DEATH_PIECES_MS = 460;
    const DEATH_MS = DEATH_BODY_MS + DEATH_PIECES_MS;
    /* A death's whole life on the layer: its pieces and ash are spawned as the
       body's beat ends and are gone a second later (debris). The portrait
       auras hold still that long (ThinkingIndicator.holdStill). */
    const DEATH_STILL_MS = DEATH_BODY_MS + 1000;
    // The impact frame. Three frames at 60Hz: long enough to read as contact,
    // short enough that it never becomes a wash over the card it belongs to.
    const CONTACT_FLASH_MS = 56;

    // Decaying shake offsets (x, y multipliers of amplitude), matching the
    // proven feel of the legacy CSS keyframes, ending at rest.
    const SHAKE_PATTERN = [
        [0, 0], [-1, -0.7], [1, 0.5], [-0.8, 0.8], [0.7, -0.5],
        [-0.6, 0.6], [0.5, -0.35], [-0.4, 0.4], [0.35, -0.3], [-0.25, 0.25], [0, 0]
    ];

    let activeScreenImpact = null;
    const activePortraitImpacts = new WeakMap();

    // ---------- Pure helpers (no DOM) ----------

    function cleanStatusTokens(status) {
        if (!status || typeof status !== 'string') return [];
        // The shared separator rule (status-segments.js). A status the summoner
        // rail split into two effects must not read as one reaction here.
        return splitStatusSegments(status)
            .map(cleanStatusToken)
            .filter(Boolean);
    }

    function diffBoards(prevCards, nextCards) {
        const prev = Array.isArray(prevCards) ? prevCards : [];
        const next = Array.isArray(nextCards) ? nextCards : [];
        const prevById = new Map(prev.map((c) => [c.instanceId, c]));
        const nextById = new Map(next.map((c) => [c.instanceId, c]));

        const deaths = prev.filter((c) => !nextById.has(c.instanceId));
        const summons = next.filter((c) => !prevById.has(c.instanceId));
        const statusChanges = [];

        for (const card of next) {
            const before = prevById.get(card.instanceId);
            if (!before) continue;
            const prevStatus = before.status || '';
            const nextStatus = card.status || '';
            if (prevStatus === nextStatus) continue;
            const prevTokens = new Set(cleanStatusTokens(prevStatus));
            const added = cleanStatusTokens(nextStatus).filter((t) => !prevTokens.has(t));
            statusChanges.push({ card, added });
        }

        return { deaths, summons, statusChanges };
    }

    function planBoardEffects(prevState, nextState) {
        if (!prevState || !nextState) return null;
        if (!prevState.player && !prevState.opponent) return null;
        const plan = {
            player: diffBoards(prevState.player?.summonedCards, nextState.player?.summonedCards),
            opponent: diffBoards(prevState.opponent?.summonedCards, nextState.opponent?.summonedCards),
        };
        const hasWork = ['player', 'opponent'].some((side) =>
            plan[side].deaths.length || plan[side].summons.length || plan[side].statusChanges.length);
        return hasWork ? plan : null;
    }

    function shakeParams(damage) {
        if (typeof damage !== 'number' || !(damage > 0)) return null;
        const amp = Math.max(2, Math.min(22, 1.5 + damage * 1.3));
        return { amp, duration: damage <= 3 ? 260 : 450 };
    }

    function numberTier(amount) {
        if (amount >= 6) return 'crit';
        if (amount >= 3) return 'med';
        return 'small';
    }

    // ---------- Environment probes ----------

    let reducedMotionQuery = null;

    function reducedMotion() {
        if (typeof window.matchMedia !== 'function') return false;
        if (!reducedMotionQuery) {
            try {
                reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
            } catch (_) {
                return false;
            }
        }
        return !!reducedMotionQuery.matches;
    }

    function canAnimate(el) {
        return !!el && typeof el.animate === 'function';
    }

    function resolveRect(target) {
        if (!target) return null;
        if (typeof target.getBoundingClientRect === 'function') {
            const rect = target.getBoundingClientRect();
            return rect && rect.width >= 0 ? rect : null;
        }
        if (typeof target.left === 'number' && typeof target.top === 'number') return target;
        return null;
    }

    function findBoardCard(instanceId) {
        if (!instanceId) return null;
        try {
            return document.querySelector(`.card[data-instance-id="${instanceId}"][data-zone="player-board"]`)
                || document.querySelector(`.card[data-instance-id="${instanceId}"][data-zone="opponent-board"]`)
                || document.querySelector(`.card[data-instance-id="${instanceId}"]`);
        } catch (_) {
            return null;
        }
    }

    // ---------- Layer management ----------

    function ensureLayerById(id) {
        let layer = document.getElementById(id);
        if (!layer) {
            layer = document.createElement('div');
            layer.id = id;
            layer.setAttribute('aria-hidden', 'true');
            document.body.appendChild(layer);
        }
        return layer;
    }

    function ensureLayer() {
        return ensureLayerById(LAYER_ID);
    }

    /* Numbers live in their own top-of-stack layer — see fx.css. */
    function ensureNumberLayer() {
        return ensureLayerById(NUMBER_LAYER_ID);
    }

    /* How far a number should travel from `startTop`, in px (negative = up).
       It normally rises, but the summoner portraits are pinned to the top
       corners: on a short page box the whole flight would end above the
       viewport and be lost. When there is no headroom it floats DOWN instead,
       and either way both ends of the path are clamped on screen. Exported so
       the travel can be asserted rather than inferred from an animation. */
    const NUMBER_EDGE_MARGIN = 8;
    const NUMBER_RISE_PX = 40;
    function numberTravel(startTop, distance = uiPx(NUMBER_RISE_PX), viewportHeight = window.innerHeight) {
        const rise = -Math.abs(distance);
        const edge = uiPx(NUMBER_EDGE_MARGIN);
        if (startTop + rise >= edge) return rise;
        const room = viewportHeight - edge - startTop;
        return Math.max(0, Math.min(Math.abs(distance), room));
    }

    function sweepStale(layer) {
        const now = Date.now();
        for (const child of [...layer.children]) {
            const born = Number(child.dataset.fxBorn || 0);
            if (!born) continue;
            const limit = child.dataset.fxPersist ? PERSIST_STALE_MS : STALE_MS;
            if (now - born > limit) child.remove();
        }
    }

    // An effect legitimately spawns several nodes on one frame (a death is a
    // ghost, a shadow, a crack and two debris clouds). Evicting strictly by
    // age let the last of those cannibalise the first, and a Firefox run lost
    // a death's own ghost before it had played. Fresh nodes are therefore
    // evicted only when nothing older is available.
    const EVICTION_GRACE_MS = 250;

    function evictOldest(layer, selector) {
        const nodes = [...(selector ? layer.querySelectorAll(selector) : layer.children)];
        if (!nodes.length) return;
        /* Cap pressure spends ordinary effects first. A persist clone stands
           in for a HIDDEN authoritative card — evicting it leaves a hole on
           the board, which is a worse loss than any spark. It still goes when
           nothing else is left, so the cap holds. */
        const ordinary = nodes.filter((node) => !node.dataset.fxPersist);
        const pool = ordinary.length ? ordinary : nodes;
        const now = Date.now();
        const stale = pool.find((node) => now - Number(node.dataset.fxBorn || 0) > EVICTION_GRACE_MS);
        (stale || pool[0]).remove();
    }

    function spawn(baseClass, extraClasses = [], layerOverride = null) {
        const layer = layerOverride || ensureLayer();
        sweepStale(layer);
        const typeCap = TYPE_CAPS[baseClass];
        if (typeCap) {
            while (layer.querySelectorAll(`.${baseClass}`).length >= typeCap) evictOldest(layer, `.${baseClass}`);
        }
        while (layer.children.length >= MAX_ACTIVE) evictOldest(layer);
        const el = document.createElement('div');
        el.className = [baseClass, ...extraClasses].filter(Boolean).join(' ');
        el.dataset.fxBorn = String(Date.now());
        layer.appendChild(el);
        return el;
    }

    function scheduleRemoval(el, afterMs) {
        setTimeout(() => { if (el.isConnected) el.remove(); }, afterMs);
    }

    function runAnimation(el, keyframes, options, removeAfterMs, fallbackClass = '') {
        let animated = false;
        if (canAnimate(el)) {
            try {
                const anim = el.animate(keyframes, options);
                animated = true;
                anim.finished
                    .then(() => { if (el.isConnected) el.remove(); })
                    .catch(() => { /* cancelled — eviction/sweep owns removal */ });
            } catch (_) { /* fall through to timed removal */ }
        }
        if (!animated && fallbackClass) el.classList.add(fallbackClass);
        scheduleRemoval(el, removeAfterMs);
    }

    // ---------- Effects ----------

    function damageNumber(target, amount, { kind = 'damage' } = {}) {
        if (typeof amount !== 'number' || !(amount > 0)) return;
        const rect = resolveRect(target);
        if (!rect) return;

        const tier = numberTier(amount);
        const classes = [`fx-number--${kind}`];
        if (tier === 'crit') classes.push('fx-number--crit');
        const layer = ensureNumberLayer();
        const el = spawn('fx-number', classes, layer);
        el.textContent = kind === 'heal' ? `+${amount}` : `-${amount}`;

        const siblings = layer.querySelectorAll('.fx-number').length - 1;
        const jitter = ((siblings % 3) - 1) * uiPx(16);
        // Clamp the birthplace too: a portrait flush to the top edge would
        // otherwise start the number off-screen before it even moves.
        const startTop = Math.max(
            uiPx(NUMBER_EDGE_MARGIN),
            Math.min(rect.top + rect.height * 0.3, window.innerHeight - uiPx(NUMBER_EDGE_MARGIN)),
        );
        el.style.left = `${rect.left + rect.width / 2 + jitter}px`;
        el.style.top = `${startTop}px`;

        const duration = tier === 'crit' ? 1100 : 900;
        // Reduced motion: fade in place — no travel, no scale pop (the CSS
        // media query cannot reach WAAPI animations, so gate here).
        const reduce = reducedMotion();
        const rise = reduce ? 0 : numberTravel(startTop, Math.max(uiPx(NUMBER_RISE_PX), rect.height * 0.45));
        const startScale = reduce ? 1 : (tier === 'crit' ? 1.6 : 1.25);
        // Per-keyframe easing: options-level easing would warp the whole
        // timeline and collapse the readable hold phase.
        const midScale = reduce ? 1 : 1.05;
        runAnimation(el, [
            { transform: `translate(-50%, 0) scale(${startScale})`, opacity: 0, easing: POP_EASING },
            { transform: `translate(-50%, ${rise * 0.25}px) scale(${midScale})`, opacity: 1, offset: 0.18, easing: 'linear' },
            { transform: `translate(-50%, ${rise * 0.75}px) scale(1)`, opacity: 1, offset: 0.7, easing: 'ease-in' },
            { transform: `translate(-50%, ${rise}px) scale(1)`, opacity: 0 },
        ], { duration }, duration + 400);
    }

    function statusFloat(target, text, color) {
        if (!text) return;
        const rect = resolveRect(target);
        if (!rect) return;
        const el = spawn('fx-status-float');
        el.textContent = text;
        if (color) el.style.color = color;
        el.style.left = `${rect.left + rect.width / 2}px`;
        el.style.top = `${rect.top + rect.height * 0.2}px`;
        const rise = reducedMotion() ? 0 : -Math.max(uiPx(28), rect.height * 0.3);
        runAnimation(el, [
            { transform: 'translate(-50%, 0)', opacity: 0, easing: POP_EASING },
            { transform: `translate(-50%, ${rise * 0.4}px)`, opacity: 1, offset: 0.25, easing: 'ease-in' },
            { transform: `translate(-50%, ${rise}px)`, opacity: 0 },
        ], { duration: 1000 }, 1400);
    }

    function impactAt(target, { strength = 1 } = {}) {
        if (reducedMotion()) return;
        const rect = resolveRect(target);
        if (!rect) return;
        const el = spawn('fx-impact-ring');
        const size = Math.max(uiPx(40), Math.min(uiPx(220), Math.hypot(rect.width, rect.height) * 0.8 * strength));
        el.style.width = `${size}px`;
        el.style.height = `${size}px`;
        el.style.left = `${rect.left + rect.width / 2}px`;
        el.style.top = `${rect.top + rect.height / 2}px`;
        runAnimation(el, [
            { transform: 'translate(-50%, -50%) scale(0.4)', opacity: 0.9 },
            { transform: 'translate(-50%, -50%) scale(1.15)', opacity: 0 },
        ], { duration: 300, easing: 'ease-out' }, 700);
    }

    // ---------- Weight primitives ----------

    function placeOver(el, rect) {
        el.style.left = `${rect.left}px`;
        el.style.top = `${rect.top}px`;
        el.style.width = `${rect.width}px`;
        el.style.height = `${rect.height}px`;
    }

    function unitDirection(from, to) {
        if (!from || !to) return { x: 0, y: 0 };
        const dx = (to.left + to.width / 2) - (from.left + from.width / 2);
        const dy = (to.top + to.height / 2) - (from.top + from.height / 2);
        const len = Math.hypot(dx, dy) || 1;
        return { x: dx / len, y: dy / len };
    }

    // A phone page box renders cards a third of their desktop size; a fixed
    // particle count reads as confetti there and spends frames on motes nobody
    // can resolve.
    function particleCount(base, rect) {
        const unit = uiPx(130);
        const scale = Math.min(1, Math.max(0.4, (rect?.height || unit) / unit));
        return Math.max(3, Math.round(base * scale));
    }

    function clamp(min, value, max) {
        return Math.max(min, Math.min(max, value));
    }

    // ---------- Moving the real card ----------

    /**
     * The wrapper that carries a board card's position inside its slot
     * (renderBoard builds `.card-slot > centerer > .card`).
     *
     * Board motion is applied HERE and never to the card or to a stand-in
     * clone. Three things follow, and all three are the point:
     *  - the real card stays in the document and stays hit-testable, so hover
     *    inspection and the touch tap-latch keep working through every
     *    flourish. A landing that hides its own card gates input, which the
     *    juice bar forbids outright;
     *  - the card's own composed transform (`--card-base-transform`, tilt,
     *    hover) is untouched, so board motion and tilt coexist instead of
     *    overwriting each other;
     *  - the slot's outline stays put, so a card visibly leaves and re-enters
     *    the space it belongs to.
     */
    function cardMover(cardEl) {
        const slot = cardEl?.closest?.('.card-slot');
        const mover = cardEl?.parentElement;
        if (!slot || !mover || mover === slot || !slot.contains(mover)) return null;
        return mover;
    }

    // One logical motion per card instance, and one physical owner per mover.
    // The instance registry is what prevents two pre-rebuild watchers from
    // resurrecting competing animations on the same replacement node.
    const activeMoves = new WeakMap();
    const activeMoveControllers = new Map();

    /**
     * Prefix every keyframe with the transform the element is ALREADY wearing.
     *
     * A WAAPI transform animation replaces the element's CSS transform for its
     * whole run, so animating an element whose transform is load-bearing snaps
     * it to the animation's own origin and back. That shipped: the phone tier
     * positions both board lanes with `transform: translateY(-50%)`, and the
     * landing's lane dip threw the entire slot row 40px down and back on every
     * phone summon — the desktop motion the design wants, at ten times the
     * amplitude, plus a placement jump. Every board transform animation goes
     * through here. Movers happen to carry no CSS transform today; the lane
     * does, and the difference is invisible until someone is on a phone.
     *
     * The resting transform is read once, at start. A rest transform that is
     * itself rotated or scaled would compose about the animation's
     * `transform-origin` rather than its own — still far better than being
     * erased, and no board element has one.
     */
    function composeWithResting(el, frames) {
        let rest = '';
        try {
            rest = getComputedStyle(el).transform || '';
        } catch (_) {
            return frames;
        }
        if (!rest || rest === 'none') return frames;
        return frames.map((frame) => (frame.transform
            ? { ...frame, transform: `${rest} ${frame.transform}` }
            : frame));
    }

    /**
     * Animate a real board card by its mover. The slot is lifted for the
     * duration so the moving card paints over its neighbours, and the lift is
     * released by a timer as well as by the animation, so no failure path can
     * strand a slot elevated.
     *
     * Survives a lane rebuild. Moving the real card buys interactivity at the
     * cost of the card's node being disposable — an authoritative snapshot
     * replaces the whole lane, and the animation dies with the old node. The
     * recorded tutorial does exactly that ~100ms into an enemy attack, which
     * left all three of its strikes frozen in their wind-up. So a rebuild
     * re-binds to the replacement card and RESUMES from the elapsed time,
     * rather than restarting or abandoning the motion.
     *
     * @param {Element} cardEl
     * @param {Keyframe[]} frames
     * @param {{origin?: string, duration?: number, delay?: number, easing?: string}} [options]
     * @returns {Animation|null} null when the card has no mover (a hand card,
     *   a portrait, a detached node) — callers treat that as "no body to move".
     */
    function moveCard(cardEl, frames, { origin = '50% 50%', ...options } = {}) {
        let mover = cardMover(cardEl);
        if (!mover || !canAnimate(mover)) return null;

        const instanceId = cardEl.dataset?.instanceId;
        const lane = cardEl.closest?.('.board-container');
        const span = Math.max(0, Number(options.duration) || 0)
            + Math.max(0, Number(options.delay) || 0);
        const startedAt = Date.now();
        let watcher = null;
        let cleanupTimer = null;
        let stopped = false;
        let attachment = null;
        let pendingCard = null;
        let candidateGeneration = 0;
        let controller = null;

        const elapsed = () => Math.max(0, Date.now() - startedAt);

        const release = (owned, cancel = false) => {
            if (!owned || owned.released) return;
            owned.released = true;
            if (attachment === owned) attachment = null;
            if (activeMoves.get(owned.host) === controller) {
                activeMoves.delete(owned.host);
            }
            owned.slot?.classList?.remove('fx-slot-lifted');
            if (cancel) {
                try {
                    owned.anim?.cancel?.();
                } catch (_) { /* presentation only */ }
            }
        };

        const stop = () => {
            if (stopped) return;
            stopped = true;
            candidateGeneration += 1;
            pendingCard = null;
            watcher?.disconnect?.();
            watcher = null;
            if (cleanupTimer) clearTimeout(cleanupTimer);
            cleanupTimer = null;
            release(attachment, true);
            if (instanceId && activeMoveControllers.get(instanceId) === controller) {
                activeMoveControllers.delete(instanceId);
            }
        };

        controller = { cancel: stop, stop };

        const attach = (host, { initial = false } = {}) => {
            if (
                stopped
                || !host
                || !canAnimate(host)
                || (!initial && elapsed() >= span)
            ) return null;

            const previous = activeMoves.get(host);
            if (previous && previous !== controller) previous.cancel?.();
            release(attachment, true);

            const slot = host.parentElement;
            if (!slot) return null;
            host.style.transformOrigin = origin;
            slot.classList.add('fx-slot-lifted');
            let anim = null;
            try {
                anim = host.animate(composeWithResting(host, frames), options);
                // Resume where the replaced node had got to.
                const currentElapsed = elapsed();
                if (currentElapsed > 0) anim.currentTime = Math.min(currentElapsed, span);
            } catch (_) {
                slot.classList.remove('fx-slot-lifted');
                return null;
            }
            const owned = {
                host,
                slot,
                anim,
                released: false,
            };
            attachment = owned;
            activeMoves.set(host, controller);
            anim.finished
                .then(() => release(owned))
                .catch(() => release(owned));
            return anim;
        };

        if (instanceId) {
            activeMoveControllers.get(instanceId)?.stop?.();
            activeMoveControllers.set(instanceId, controller);
        }

        const first = attach(mover, { initial: true });
        if (!first) {
            stop();
            return null;
        }

        if (lane && instanceId && typeof MutationObserver === 'function') {
            const considerReplacement = () => {
                if (stopped) return;
                if (elapsed() >= span) {
                    stop();
                    return;
                }

                const liveCard = findBoardCard(instanceId);
                const next = cardMover(liveCard);
                if (!liveCard || !next) {
                    /* The CARD is gone. Release on that, not on the mover's node
                       having vanished: a full-board rebuild used to guarantee
                       the two were the same event, and the reconciler keeps
                       movers forever, so `mover.isConnected` is now permanently
                       true and this motion would run on into whoever is seated
                       here next. (renderBoard ends it at the slot as well; this
                       keeps the controller's own bookkeeping honest instead of
                       depending on a collaborator to tidy up after it.) */
                    release(attachment, true);
                    if (pendingCard && !pendingCard.isConnected) {
                        pendingCard = null;
                        candidateGeneration += 1;
                    }
                    return;
                }

                const artPending = liveCard.classList.contains('card-summon-art-pending');
                // Attribute mutations caused by the motion itself (slot lift,
                // transform origin, natural release) are observable too. A
                // ready node already chosen as `mover` is not a replacement
                // and must never restart merely because its Animation ended.
                // The sole exception is that same node having entered the
                // presentation gate and now becoming ready again.
                if (
                    !artPending
                    && next === mover
                    && mover.isConnected
                    && pendingCard !== liveCard
                ) return;

                if (artPending) {
                    // A failed raster transfer deliberately withholds the new
                    // card. Never run its body motion invisibly: release the
                    // disposable predecessor and observe Board's presentation
                    // gate. Attribute observation is the fallback if Board has
                    // no waiter or its promise settles before the class does.
                    release(attachment, true);
                    if (pendingCard === liveCard) return;
                    pendingCard = liveCard;
                    const generation = ++candidateGeneration;
                    const waitForPresentation = Board?.waitForCardPresentation;
                    if (typeof waitForPresentation !== 'function') return;

                    let presentation;
                    try {
                        presentation = waitForPresentation(instanceId);
                    } catch (_) {
                        return;
                    }
                    void Promise.resolve(presentation).then(() => {
                        if (stopped || generation !== candidateGeneration) return;
                        if (elapsed() >= span) {
                            stop();
                            return;
                        }
                        const currentCard = findBoardCard(instanceId);
                        const currentMover = cardMover(currentCard);
                        if (
                            !currentCard
                            || !currentMover
                            || currentCard.classList.contains('card-summon-art-pending')
                        ) return;
                        pendingCard = null;
                        candidateGeneration += 1;
                        mover = currentMover;
                        attach(currentMover);
                    }).catch(() => {
                        if (generation === candidateGeneration) pendingCard = null;
                    });
                    return;
                }

                pendingCard = null;
                candidateGeneration += 1;
                mover = next;
                attach(next);
            };

            watcher = new MutationObserver(considerReplacement);
            watcher.observe(lane, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['class', 'data-card-art-presentation'],
            });
        }

        // A controller may outlive an individual disposable Animation while
        // it waits for a replacement, but never the original motion's absolute
        // lifetime (plus a small cleanup margin).
        cleanupTimer = setTimeout(stop, span + 400);
        return first;
    }

    const activeLaneReactions = new WeakMap();

    /**
     * The board taking the weight. A proportional dip of the lane the event
     * happened in — not the whole view: `#game-view` carries the battlefield
     * art and is contractually never transformed, and the middle bar carries
     * End Turn, which must not move under a pointer. Translation only, never
     * scale or filter, so no card raster is ever resampled by it.
     *
     * Composed with the lane's resting transform, and that is not optional
     * here: the phone tier positions both lanes with `position: fixed;
     * transform: translateY(-50%)`, so an uncomposed dip REPLACED that
     * placement for its whole run and threw the slot row ~40px down and back
     * on every phone summon — a ten-times-too-large lurch of the same motion
     * that reads correctly at 4.5px on desktop.
     */
    /**
     * AN UNTARGETED SPELL — a surge, not a strike.
     *
     * A spell with no declared target has no arrival: there is nothing for it
     * to land on. The shared target resolver falls back to a portrait when
     * `targetType` is absent, which is right for an attack and wrong here, and
     * it made every non-targetable cast flash on the enemy summoner — "it
     * blinks on the enemy summoner ... the untargetable one". Suppressing the
     * strike stopped the lie but left these spells with no acknowledgement at
     * all, and an untargetable spell may affect an unbounded number of things,
     * so pointing anywhere is a smaller truth than pointing everywhere.
     *
     * So this beat is deliberately DIRECTIONLESS. It begins at the caster —
     * the one place the effect is genuinely from — and travels outward across
     * the whole board as an expanding front, fading as it goes. Nothing recoils,
     * nothing is singled out, and the screen does not shake toward anything.
     *
     * Presentation only, and strictly downstream: it observes an authoritative
     * cast and draws. Failure to draw is swallowed — a missing flourish must
     * never look like a different gameplay result.
     *
     * @param {Element} originEl        the caster; the surge starts here
     * @param {object}  options
     * @param {number}  options.power   0..1-ish weight, from the spell's cost
     * @param {number}  options.delayMs wait for a travelling card to land
     */
    function spellSurge(originEl, { power = 1, delayMs = 0 } = {}) {
        const strength = clamp(0.5, Number(power) || 1, 1.9);

        const surge = () => {
            /* No early return on a missing #fx-layer: `spawn` creates it on
               demand, exactly as the strike relies on. Guarding here meant the
               very first effect of a match drew nothing. */
            /* The caster is the ORIGIN, not the subject. If it has gone (the
               card left the hand, the board re-rendered) the surge still runs
               from the centre of the board, because the spell still happened. */
            /* An element must still be in the page; a remembered box (where a
               dragged spell was spent) is taken as it is. */
            const isElement = !!originEl && typeof originEl.getBoundingClientRect === 'function';
            const rect = originEl && (!isElement || originEl.isConnected) ? resolveRect(originEl) : null;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const cx = rect && rect.width > 0 ? rect.left + rect.width / 2 : vw / 2;
            const cy = rect && rect.height > 0 ? rect.top + rect.height / 2 : vh / 2;

            /* Reduced motion still has to SAY a spell was cast — losing the only
               acknowledgement loses information, not just polish. A still bloom
               at the origin, no travel and no expansion. */
            if (reducedMotion()) {
                const quiet = spawn('fx-surge-mark');
                quiet.style.left = `${cx}px`;
                quiet.style.top = `${cy}px`;
                scheduleRemoval(quiet, 560);
                return;
            }

            /* A legibility floor in VIEWPORT terms, like the strike: the surge
               must read the same whether it starts at a portrait or a card. */
            const unit = Math.min(vw, vh) / 100;

            /* ANTICIPATION — a short inward gather before anything leaves. */
            const gather = spawn('fx-surge-gather');
            gather.style.left = `${cx}px`;
            gather.style.top = `${cy}px`;
            gather.style.setProperty('--surge-size', `${unit * 16 * strength}px`);
            scheduleRemoval(gather, 260);

            /* RELEASE — concentric fronts, staggered so they read as one
               travelling wave rather than three rings appearing at once. The
               reach is the distance to the furthest corner, so the front
               genuinely crosses the board instead of stopping short of it. */
            const reach = Math.max(
                Math.hypot(cx, cy),
                Math.hypot(vw - cx, cy),
                Math.hypot(cx, vh - cy),
                Math.hypot(vw - cx, vh - cy),
            );
            for (let i = 0; i < 3; i += 1) {
                const front = spawn('fx-surge-front');
                front.style.left = `${cx}px`;
                front.style.top = `${cy}px`;
                front.style.setProperty('--surge-reach', `${reach * (0.72 + i * 0.14)}px`);
                front.style.animationDelay = `${110 + i * 90}ms`;
                scheduleRemoval(front, 900 + i * 90);
            }

            /* FOLLOW-THROUGH — a glow at the origin that outlives the fronts,
               so the beat settles instead of stopping. */
            const after = spawn('fx-surge-mark');
            after.style.left = `${cx}px`;
            after.style.top = `${cy}px`;
            scheduleRemoval(after, 780);
        };

        if (delayMs > 0) setTimeout(surge, delayMs);
        else surge();
    }

    /**
     * The moment a Spell LANDS on its target.
     *
     * A targeted Spell had no payoff at all: the card left the hand, the effect
     * applied, and the target never acknowledged being hit. `glidePlay` is
     * travel only, and the local caster does not even get that (enemy-replay
     * deliberately skips your own actions, because you performed the drag).
     * Whatever carried the spell across the board, the ARRIVAL is what sells
     * it, so the beat lives here and both callers share it.
     *
     * The composition is the vocabulary in order, not a pile of particles:
     * a contact flash on the frame of impact, an impact ring, a burst thrown
     * outward, the target itself recoiling, and screen shake scaled to the
     * spell's weight. Nothing here reads game state or changes it — it is
     * handed a target and a strength and draws.
     *
     * @param {Element} targetEl        what got hit
     * @param {object}  options
     * @param {number}  options.power   0..1-ish weight, from the spell's cost
     * @param {number}  options.delayMs wait for a travelling projectile to land
     */
    function spellStrike(targetEl, { power = 1, delayMs = 0 } = {}) {
        if (!targetEl) return;
        const strength = clamp(0.5, Number(power) || 1, 1.9);

        const strike = () => {
            if (!targetEl.isConnected) return;
            const rect = resolveRect(targetEl);
            if (!rect || !(rect.width > 0)) return;

            /* Reduced motion still has to SAY the spell hit something — losing
               the only acknowledgement is losing information, not just polish.
               Every primitive above opts itself out under the preference, so
               the quiet path draws its own mark: a still glow over the target
               that fades, with no travel, shake, scatter or scale. */
            if (reducedMotion()) {
                const mark = spawn('fx-spell-mark');
                mark.style.left = `${rect.left + rect.width / 2}px`;
                mark.style.top = `${rect.top + rect.height / 2}px`;
                mark.style.width = `${rect.width}px`;
                mark.style.height = `${rect.height}px`;
                scheduleRemoval(mark, 520);
                return;
            }

            /* A LEGIBILITY FLOOR, not a percentage of the target.
               Sizing every radius off the target's own box made the beat
               invisible on a small one: captured against a summoner portrait,
               the whole strike was a faint ring and nothing else — it did not
               read as an impact at all. A spell landing is the same event
               whatever it lands on, so the effect has a minimum presence in
               VIEWPORT terms and only grows from there. */
            const unit = Math.min(window.innerWidth, window.innerHeight) / 100;
            const spanX = Math.max(rect.width * 0.62, unit * 13) * strength;
            const spanY = Math.max(rect.height * 0.46, unit * 10) * strength;

            /* STAGGERED, and with a TAIL.
               Captured in a live battle, every primitive fired on the same
               frame and the whole strike was over inside ~50ms: one frame had a
               ring and a flash, the next had nothing. A beat that brief does not
               read as an impact at all — it reads as a glitch. Groups never
               animate in lockstep, and a hit needs follow-through, so the
               contact lands first and the rest arrive behind it over ~90ms,
               with a glow that outlives them all and fades. */
            const lingering = spawn('fx-spell-mark');
            lingering.style.left = `${rect.left + rect.width / 2}px`;
            lingering.style.top = `${rect.top + rect.height / 2}px`;
            lingering.style.width = `${Math.max(rect.width, unit * 16)}px`;
            lingering.style.height = `${Math.max(rect.height, unit * 14)}px`;
            /* Measured off a 25fps recording: the strike peaked and was gone
               inside ~80ms, and this tail was too faint to read against an
               already-purple portrait. It is the only part of the beat this
               function owns outright — the rest are shared primitives whose
               timing also serves attacks — so the tail is what carries the
               follow-through, and it has to be legible on its own. */
            runAnimation(lingering, [
                { opacity: 0, transform: 'translate(-50%, -50%) scale(0.66)' },
                { opacity: 1, transform: 'translate(-50%, -50%) scale(1.08)', offset: 0.1 },
                { opacity: 0.92, transform: 'translate(-50%, -50%) scale(1.02)', offset: 0.3 },
                { opacity: 0.55, transform: 'translate(-50%, -50%) scale(1.05)', offset: 0.62 },
                { opacity: 0, transform: 'translate(-50%, -50%) scale(1.18)' },
            ], { duration: 900, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)' }, 940);

            contactFlash(rect, { strength: 0.95 + 0.15 * strength });
            impactAt(targetEl, { strength: strength * 1.35 });
            setTimeout(() => {
                if (!targetEl.isConnected) return;
                burst(targetEl, {
                    count: Math.round(12 + 8 * strength),
                    radiusX: spanX,
                    radiusY: spanY,
                });
            }, 40);
            setTimeout(() => {
                if (!targetEl.isConnected) return;
                shockwave(targetEl, { strength: strength * 1.25 });
            }, 65);
            // Scatter thrown off the point of contact, so the hit has debris as
            // well as light — the difference between a flash and a strike.
            setTimeout(() => {
                if (!targetEl.isConnected) return;
                debris(targetEl, {
                    count: Math.round(6 + 5 * strength),
                    palette: 'dust',
                    origin: 'center',
                    spread: 1.15,
                    power: strength,
                });
            }, 95);
            // The target acknowledges the hit; the board around it does not,
            // because a Spell is not a body colliding with another body.
            boardReaction(targetEl, strength * 0.9);
            // Proportional, never uniform: a 1-cost cantrip must not shake the
            // screen like a finisher.
            if (strength > 1.1) screenShake(Math.round(strength * 3));
        };

        if (delayMs > 0) setTimeout(strike, delayMs);
        else strike();
    }

    // ---------- The ally beat ----------

    /* A touch is shorter on the way in than a strike. The gather is the whole
       anticipation, and the bloom lands where the strike's contact frame would
       — so a lunge that ends in a touch opens the gather this far BEFORE its
       contact frame and the bloom arrives with the body. */
    const TOUCH_GATHER_MS = 220;
    const TOUCH_BLOOM_AT_MS = 140;

    /**
     * Light drawn IN to a target — the anticipation of an enchantment, and the
     * exact inverse of `impactAt`, whose ring is thrown OUT of a blow. A soft
     * disc a little wider than the target that closes onto it and dims as it
     * arrives, so the bloom that follows reads as the light having got there.
     */
    function gatherAt(rect, { strength = 1, unit = 1 } = {}) {
        if (reducedMotion()) return;
        const el = spawn('fx-touch-gather');
        /* The same legibility floor as the strike: sized in viewport terms
           first, so a summoner portrait gathers as much light as a card. */
        const size = Math.max(Math.hypot(rect.width, rect.height) * 0.8, unit * 14) * strength;
        el.style.width = `${size}px`;
        el.style.height = `${size}px`;
        el.style.left = `${rect.left + rect.width / 2}px`;
        el.style.top = `${rect.top + rect.height / 2}px`;
        runAnimation(el, [
            { transform: 'translate(-50%, -50%) scale(1.45)', opacity: 0 },
            { transform: 'translate(-50%, -50%) scale(1.22)', opacity: 0.95, offset: 0.3 },
            { transform: 'translate(-50%, -50%) scale(0.86)', opacity: 0.7, offset: 0.82 },
            { transform: 'translate(-50%, -50%) scale(0.76)', opacity: 0 },
        ], { duration: TOUCH_GATHER_MS, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }, TOUCH_GATHER_MS + 300, 'fx-overlay-fallback');
    }

    /**
     * A band of light passing UP through the target — the direction is the
     * whole message. A blow travels outward and down (the ring, the shockwave
     * on the floor, dust falling); an enchantment climbs. A soft horizontal
     * glint, foot to head, gone before it can read as a pane over the text.
     */
    function sweepAt(rect, { strength = 1, unit = 1 } = {}) {
        if (reducedMotion()) return;
        const el = spawn('fx-touch-sweep');
        const width = Math.max(rect.width * 1.15, unit * 12) * Math.min(strength, 1.4);
        const height = Math.max(rect.height * 0.16, unit * 2.4);
        el.style.left = `${rect.left + rect.width / 2}px`;
        el.style.top = `${rect.top + rect.height}px`;
        el.style.width = `${width}px`;
        el.style.height = `${height}px`;
        const climb = rect.height + height * 0.5;
        const risen = (fraction) => `calc(-50% - ${(climb * fraction).toFixed(1)}px)`;
        el.style.setProperty('--sweep-climb', `${(-climb).toFixed(1)}px`);
        runAnimation(el, [
            { transform: 'translate(-50%, -50%) scaleX(0.7)', opacity: 0 },
            { transform: `translate(-50%, ${risen(0.18)}) scaleX(1)`, opacity: 0.95, offset: 0.2 },
            { transform: `translate(-50%, ${risen(0.7)}) scaleX(1.02)`, opacity: 0.8, offset: 0.68 },
            { transform: `translate(-50%, ${risen(1)}) scaleX(0.9)`, opacity: 0 },
        ], { duration: 460, easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' }, 760, 'fx-overlay-fallback');
    }

    /**
     * Motes RISING off a target — matter given, where `debris` is matter
     * thrown. They leave the lower half of the target spread across its width,
     * climb past its top edge, slow, and go out. CSS-animated from custom
     * properties like every other mote, so a dozen cost no per-frame JS.
     */
    function rise(rect, { count = 10, power = 1 } = {}) {
        if (reducedMotion()) return;
        const el = spawn('fx-rise');
        el.style.left = `${rect.left + rect.width / 2}px`;
        el.style.top = `${rect.top + rect.height * 0.72}px`;
        const motes = particleCount(count, rect);
        const spread = Math.max(uiPx(20), rect.width * 0.5);
        const climb = Math.max(uiPx(40), rect.height * 0.85) * power;
        for (let i = 0; i < motes; i += 1) {
            const mote = document.createElement('span');
            mote.className = 'fx-rise-mote';
            // Seated evenly across the target, then jittered, so the field
            // never reads as a column or a rosette.
            const t = (i + Math.random() * 0.8) / motes;
            const x = (t - 0.5) * 2;
            mote.style.left = `${(x * spread).toFixed(1)}px`;
            mote.style.top = `${((Math.random() - 0.5) * rect.height * 0.3).toFixed(1)}px`;
            // A slight outward lean on the way up: light leaving, not a fountain.
            mote.style.setProperty('--dx', `${(x * spread * 0.35 + (Math.random() - 0.5) * uiPx(10)).toFixed(1)}px`);
            mote.style.setProperty('--dy', `${(-climb * (0.6 + Math.random() * 0.55)).toFixed(1)}px`);
            mote.style.setProperty('--s', (0.6 + Math.random() * 0.9).toFixed(2));
            mote.style.animationDelay = `${Math.round(Math.random() * 220)}ms`;
            mote.style.animationDuration = `${Math.round(720 + Math.random() * 300)}ms`;
            el.appendChild(mote);
        }
        scheduleRemoval(el, 1400);
    }

    /**
     * An ally lifting a breath under the touch and settling — the real card,
     * moved, where `knockBack` would throw it. Straight up and back, never
     * sideways: a shove is the one thing this must not be. Board cards only;
     * a portrait or a deck pile keeps its raster untouched, as everywhere else.
     */
    function lift(targetEl, strength = 1) {
        if (reducedMotion()) return;
        const rect = resolveRect(targetEl);
        if (!rect || !(rect.height > 0)) return;
        const up = clamp(uiPx(4), rect.height * 0.06 * strength, uiPx(12));
        moveCard(targetEl, [
            { transform: 'translate(0px, 0px)', easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)' },
            { transform: `translate(0px, ${(-up).toFixed(1)}px)`, offset: 0.34, easing: 'ease-in-out' },
            { transform: `translate(0px, ${(-up * 0.7).toFixed(1)}px)`, offset: 0.58, easing: SETTLE_EASING },
            { transform: 'translate(0px, 0px)' },
        ], { duration: 560, easing: 'linear' });
    }

    /**
     * The ally beat itself: a gather, then — on the bloom frame — the
     * moonlight mark, motes rising and the ally lifting. `spellTouch` fires it
     * when a spell arrives; a friendly lunge fires it under the character's
     * arrival, timed so the bloom lands on the contact frame.
     *
     * `target` is the element the beat is drawn on, or a captured rect when
     * the live body is gone (an accepted action still lands visibly where it
     * was aimed, like `impactReaction`). Only an element can be lifted.
     */
    function touchAt(target, { strength = 1 } = {}) {
        const el = target && typeof target.isConnected === 'boolean' ? target : null;
        if (el && !el.isConnected) return;
        const rect = resolveRect(target);
        if (!rect || !(rect.width > 0)) return;
        const unit = Math.min(window.innerWidth, window.innerHeight) / 100;

        const bloom = (at) => {
            const mark = spawn('fx-spell-mark', ['fx-spell-mark--touch']);
            mark.style.left = `${at.left + at.width / 2}px`;
            mark.style.top = `${at.top + at.height / 2}px`;
            mark.style.width = `${Math.max(at.width, unit * 16)}px`;
            mark.style.height = `${Math.max(at.height, unit * 14)}px`;
            return mark;
        };

        /* Reduced motion still has to SAY the spell reached this ally — the
           still mark, no gather, no motes, no lift. */
        if (reducedMotion()) {
            scheduleRemoval(bloom(rect), 520);
            return;
        }

        gatherAt(rect, { strength, unit });
        setTimeout(() => {
            /* THE BLOOM LANDS ON THE LIVE BODY, wherever it is now. The ally
               can be re-seated while the light gathers: a hand re-fans the
               moment the cast leaves it (a fresh node in a new place), and a
               snapshot rebuilds a lane. Measured live: on the caster's own
               hand card the gather drew and the bloom — gated on the
               original node — never did. So the body is found again by its
               instance id and measured now; and if it left the table
               entirely, the bloom lands where it was, because the spell still
               reached it there. Only the lift needs a live card. */
            const live = el && (el.isConnected ? el : findBoardCard(el.dataset?.instanceId));
            const at = (live && resolveRect(live)) || rect;
            const mark = bloom(at.width > 0 ? at : rect);
            /* Swells a little past its size and settles, then outlives the
               motes: the follow-through is what makes it a beat and not a
               blink. Slower on the way in than the strike's tail, because
               nothing here is a collision. */
            runAnimation(mark, [
                { opacity: 0, transform: 'translate(-50%, -50%) scale(0.7)' },
                { opacity: 1, transform: 'translate(-50%, -50%) scale(1.06)', offset: 0.14 },
                { opacity: 0.9, transform: 'translate(-50%, -50%) scale(1.02)', offset: 0.36 },
                { opacity: 0.5, transform: 'translate(-50%, -50%) scale(1.06)', offset: 0.66 },
                { opacity: 0, transform: 'translate(-50%, -50%) scale(1.16)' },
            ], { duration: 920, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)' }, 960);
            const body = at.width > 0 ? at : rect;
            sweepAt(body, { strength, unit });
            rise(body, { count: Math.round(10 + 8 * strength), power: strength });
            if (live?.isConnected) lift(live, strength);
        }, TOUCH_BLOOM_AT_MS);
    }

    /**
     * The moment a Spell REACHES AN ALLY.
     *
     * `spellStrike` is the vocabulary of a blow: the flash at the foot, the
     * ring, sparks blown outward, the shockwave across the floor, dust, the
     * lane taking the weight, the shake. Drawn on the caster's OWN unit or
     * summoner it lied about the relationship — "it looks like a hit
     * regardless" — and a spell cast on your own side is usually anything
     * but. Usually: the storyteller resolves it later, and it may heal, bless,
     * transform, consume or wound. So this is not a heal either — no green, no
     * plus, no chime. It is the neutral language of ENCHANTMENT: light drawn
     * IN rather than blown out, motes that RISE rather than dust that falls, a
     * body that LIFTS rather than recoils, a board that does not flinch.
     * Positive-leaning, still open.
     *
     * Presentation only, and strictly downstream, exactly like the strike: it
     * observes an authoritative cast and draws. Failure to draw is swallowed.
     *
     * @param {Element} targetEl          the ally the spell reached
     * @param {object}  [options]
     * @param {number}  [options.power]   0..1-ish weight, from the spell's cost
     * @param {number}  [options.delayMs] wait for a travelling projectile to land
     */
    function spellTouch(targetEl, { power = 1, delayMs = 0 } = {}) {
        if (!targetEl) return;
        const strength = clamp(0.5, Number(power) || 1, 1.9);
        const touch = () => touchAt(targetEl, { strength });
        if (delayMs > 0) setTimeout(touch, delayMs);
        else touch();
    }

    /**
     * A card being DRAWN: a cardback leaves the deck and becomes the card.
     *
     * The old draw was a 340ms opacity fade — nothing travelled, so the deck
     * never acted and three draws read as three fades. This gives the beat a
     * SOURCE.
     *
     * The live card is never transformed. `cards.css` keeps every live card's
     * resting transform fixed so its art/text raster is not resampled, so the
     * travel is a clone on the FX layer (the same thing glidePlay already does
     * for a played spell) and the real card is simply revealed underneath it as
     * the clone lands.
     *
     * @param {Element} deckEl  the deck the card comes from
     * @param {HTMLElement} cardEl  the live card, already in its seat
     * @param {{index?: number}} options index within the drawn cohort, for stagger
     */
    function drawCard(deckEl, cardEl, { index = 0 } = {}) {
        if (!cardEl) return null;
        // Reduced motion keeps the CSS fade; never leave the card hidden.
        if (!deckEl || reducedMotion()) {
            cardEl.classList.remove('fx-draw-hold');
            cardEl.style.visibility = '';
            return null;
        }
        const from = resolveRect(deckEl);
        const to = resolveRect(cardEl);
        if (!from || !to || !(from.width > 0) || !(to.width > 0)) {
            cardEl.classList.remove('fx-draw-hold');
            cardEl.style.visibility = '';
            return null;
        }

        /* Groups never animate in lockstep. Short enough that a five-card
           opening hand still settles well inside a second.
           85ms, not 70: at 70 a five-card draw put up to five clones in the air
           at once, and on a phone that reads as a swarm rather than a deal
           ("multicard draw anim messy"). 85 separates the flights enough to be
           read in order while a full hand still lands in 4*85 + 380 = 720ms. */
        const delay = Math.max(0, index) * 85;
        const duration = 380;

        /* THE ARRIVAL BELONGS TO THE CARD, NOT TO THIS NODE. A snapshot can
           rebuild the hand while the card is in the air; renderPlayerHand then
           keeps the traveller and hands the arrival to the rebuilt seat, held
           (cards.js, "A DRAW STILL IN THE AIR KEEPS FLYING"). The launch and
           the reveal below therefore act on whichever seat holds this card
           when they run, and `fxDrawInFlight` says, until the reveal, that
           the arrival is still owed. */
        const instanceId = cardEl.dataset?.instanceId || '';
        const liveCard = () => {
            if (cardEl.isConnected || !instanceId) return cardEl;
            const hand = document.getElementById('player-hand');
            return [...(hand?.querySelectorAll('.card[data-instance-id]') || [])]
                .find((el) => el.dataset.instanceId === instanceId) || cardEl;
        };
        cardEl.dataset.fxDrawInFlight = '1';

        /* ONE OWNER PER ARRIVAL. Once the flight commits, the CSS draw-in fade
           comes off the live card — the travel owns the whole beat. The
           fallback paths above return before this line, so a draw that cannot
           fly keeps the fade as its only beat. */
        cardEl.classList.remove('card-draw-in');
        /* Hidden only while its clone is in flight. The class carries the
           !important that outranks the art-grace animation's forwards
           `visibility: inherit` fill — an inline hold alone is overruled the
           moment the hold outlives the 450ms grace (fx.css explains). The
           inline value stays as belt-and-braces for style-less contexts. */
        cardEl.classList.add('fx-draw-hold');
        cardEl.style.visibility = 'hidden';
        /* ARRIVAL, not a pop-in. The clone reaches the seat and the real card
           simply appeared there — no follow-through, so a hand of five read as
           five things blinking on. A short opacity tail gives each card the
           settle its flight implies.

           The tail is a WAAPI effect, DELIBERATELY not a class. A class-based
           tail lost the `animation` property to `.card.card-draw-in`, and
           then to the art-grace gate `.card:has(.card-art-loading)` — each at
           higher specificity, each restarting the 35% dip from scratch the
           moment it released the property ("the cards flicker mid-move", and
           the Firefox-only late dip the draw-travel oracle caught). A WAAPI
           opacity effect composites above every CSS animation, so no future
           stylesheet rule can hijack or restart it. Opacity only — transform
           stays untouched per the raster rule. Runs exactly once; the safety
           reveal below only guarantees visibility. */
        let settled = false;
        const reveal = () => {
            const seat = liveCard();
            for (const el of new Set([cardEl, seat])) {
                el.classList.remove('fx-draw-hold');
                el.style.visibility = '';
                delete el.dataset.fxDrawInFlight;
            }
            if (reducedMotion() || settled) return;
            settled = true;
            try {
                seat.animate?.(
                    [{ opacity: 0.35 }, { opacity: 1 }],
                    { duration: 190, easing: 'ease-out' },
                );
            } catch (_) { /* the tail is optional; the card is not */ }
        };

        /* REVERTED (owner: "cards draw with their other sides… the worst
           version… please revert"): the hold-for-face variant kept the
           cardback ON the seat until the art settled, which read as cards
           arriving back-side-up. The reveal returns to the flight's contact
           point unconditionally; art readiness is now handled UPSTREAM —
           renderPlayerHand delays the whole deal's launch (bounded) until the
           drawn faces' art has settled, so the face is ready BEFORE it flies
           rather than loading mid-draw. */
        const launch = () => {
            // The seat that holds the card now (a rebuild may have replaced
            // the node); a card that left the hand has nothing to fly to, and
            // the safety reveal below still clears its marks.
            const seat = liveCard();
            if (!seat.isConnected) return;
            let flew = null;
            try {
                /* THE CARD FLIES FACE-UP. The traveller used to be the deck's
                   cardback, which the owner read as cards being drawn "turned
                   on their back". With the deal's launch gated on settled art
                   (renderPlayerHand), the face is complete before any flight
                   begins — so the flight carries THE FACE itself, sliding out
                   of the deck as one finished physical card. No back, no flip:
                   the live card is revealed under the identical arriving face
                   while the traveller fades. */
                const faceClone = sanitizeClone(seat);
                faceClone.classList.remove('fx-draw-hold', 'card-draw-in');
                faceClone.style.visibility = '';
                /* CARD-SHAPED, not deck-shaped. glidePlay sizes its container
                   from the from-rect, and the deck WRAPPER's box (aspect
                   ~0.84) is not a card box (0.554) — laid out there, the face
                   clone's cover-cropped frame and var-sized internals
                   disagreed and the flight opened as a cropped, oversized
                   mess. Depart from the deck's CENTRE in the live card's own
                   layout box (offset dims — the fan rotation inflates
                   bounding rects). */
                const seatW = (seat instanceof HTMLElement ? seat.offsetWidth : 0) || to.width;
                const seatH = (seat instanceof HTMLElement ? seat.offsetHeight : 0) || to.height;
                const fromSeat = {
                    left: from.left + from.width / 2 - seatW / 2,
                    top: from.top + from.height / 2 - seatH / 2,
                    width: seatW,
                    height: seatH,
                };
                faceClone.style.width = '100%';
                faceClone.style.height = '100%';
                flew = glidePlay({ contentEl: faceClone, from: fromSeat, to: seat, duration });
                /* A DRAW is not a played card, and it must not be mistaken for
                   one. `.fx-glide` is the marker three board-impact contracts
                   read as "a stand-in body is standing in for the real card" —
                   game-juice.spec.js asserts zero of them during a summon
                   landing, and a draw arriving in that window counted as 30.
                   Tag the traveller as its own beat so a concurrent draw can
                   never be read as a summon's stand-in. */
                const clone = document.getElementById('fx-layer')?.lastElementChild;
                if (flew && clone && clone.classList.contains('fx-glide')) {
                    clone.classList.remove('fx-glide');
                    clone.classList.add('fx-draw-travel');
                    // Which card it carries: a hand rebuild keeps it by this.
                    if (instanceId) clone.dataset.fxDrawFor = instanceId;
                }
            } catch (_) { /* decoration must never lose a card */ }
            // Reveal as the clone arrives, not after it fades.
            setTimeout(reveal, flew ? Math.round(duration * 0.82) : 0);
        };

        if (delay > 0) setTimeout(launch, delay);
        else launch();
        /* A belt-and-braces reveal: whatever happens to the flight, the card
           becomes visible. An invisible card is a lost card. */
        setTimeout(reveal, delay + duration + 260);
        return true;
    }

    function boardReaction(cardEl, strength = 1) {
        if (reducedMotion()) return;
        const lane = cardEl?.closest?.('.board-container');
        if (!lane || !canAnimate(lane)) return;
        activeLaneReactions.get(lane)?.cancel?.();

        const dip = uiPx(clamp(2, 4.5 * strength, 8));
        lane.classList.add('fx-lane-reacting');
        let anim = null;
        try {
            anim = lane.animate(composeWithResting(lane, [
                { transform: 'translateY(0px)', easing: 'cubic-bezier(0.3, 0, 0.2, 1)' },
                { transform: `translateY(${dip.toFixed(1)}px)`, offset: 0.16, easing: 'ease-out' },
                { transform: `translateY(${(-dip * 0.34).toFixed(1)}px)`, offset: 0.52, easing: SETTLE_EASING },
                { transform: 'translateY(0px)' },
            ]), { duration: 340, easing: 'linear' });
        } catch (_) {
            lane.classList.remove('fx-lane-reacting');
            return;
        }
        activeLaneReactions.set(lane, anim);
        const release = () => {
            if (activeLaneReactions.get(lane) !== anim) return;
            activeLaneReactions.delete(lane);
            lane.classList.remove('fx-lane-reacting');
        };
        anim.finished.then(release).catch(release);
        setTimeout(release, 740);
    }

    // dust: the board kicked up. ash: a body burning out. (A death's actual
    // BREAK is carried by real pieces — see breakBody — so there is no third
    // "shard" mote, which read as ash at card scale anyway.)
    const DEBRIS_PALETTES = { dust: 'dust', ash: 'ash' };

    /**
     * Matter thrown off an impact, on a gravity arc — dust kicked out of the
     * board by a landing, ash off a body that just fell. Distinct from `burst`,
     * whose sparks fly a flat radial pattern: these rise, slow, and drop.
     * Each mote is CSS-animated from its own custom properties, so a dozen of
     * them cost no per-frame JS.
     */
    function debris(target, { count = 10, palette = 'dust', origin = 'base', spread = 1, power = 1 } = {}) {
        if (reducedMotion()) return;
        const rect = resolveRect(target);
        if (!rect || !(rect.width > 0)) return;

        const el = spawn('fx-debris', [`fx-debris--${DEBRIS_PALETTES[palette] || 'dust'}`]);
        el.style.left = `${rect.left + rect.width / 2}px`;
        // 'base' means the card's FOOT — its bottom edge, where it meets the
        // table. Anything above that is inside the card, and the fx layer sits
        // over every card, so motes thrown from 0.92 of the height rained down
        // the text panel instead of off the board.
        el.style.top = `${rect.top + rect.height * (origin === 'base' ? 1 : 0.5)}px`;

        const motes = particleCount(count, rect);
        // Past the card's own width: dust that stops at the card's edge reads
        // as belonging to the card, not as something the board threw off.
        const reach = Math.max(uiPx(28), rect.width * 1.35) * spread;
        for (let i = 0; i < motes; i += 1) {
            const mote = document.createElement('span');
            mote.className = 'fx-mote';
            // Fan evenly across the outward arc, then jitter, so the spray
            // never reads as a rosette while still covering both sides.
            const t = (i + Math.random() * 0.7) / motes;
            const angle = Math.PI * (0.06 + t * 0.88);
            const speed = 0.6 + Math.random() * 0.7;
            mote.style.setProperty('--dx', `${(Math.cos(angle) * reach * speed).toFixed(1)}px`);
            // Kept low: dust that arcs half a card high climbs back over the
            // card's own face, where it reads as specks on the artwork.
            mote.style.setProperty('--dy-peak', `${(-rect.height * (0.07 + 0.13 * speed) * power).toFixed(1)}px`);
            mote.style.setProperty('--dy-end', `${(rect.height * (0.04 + 0.14 * Math.random()) * power).toFixed(1)}px`);
            mote.style.setProperty('--s', (0.5 + Math.random() * 0.9).toFixed(2));
            mote.style.setProperty('--rot', `${Math.round((Math.random() - 0.5) * 220)}deg`);
            // Independent width/height and an irregular blob radius: a field
            // of identical circles reads as popcorn pearls, not as grit.
            mote.style.setProperty('--w', (0.7 + Math.random() * 0.75).toFixed(2));
            mote.style.setProperty('--h', (0.7 + Math.random() * 0.75).toFixed(2));
            const r = () => Math.round(34 + Math.random() * 32);
            mote.style.borderRadius = `${r()}% ${r()}% ${r()}% ${r()}% / ${r()}% ${r()}% ${r()}% ${r()}%`;
            mote.style.animationDelay = `${Math.round(Math.random() * 70)}ms`;
            el.appendChild(mote);
        }
        scheduleRemoval(el, 1000);
    }

    /**
     * A ring travelling out across the board's floor. Flattened on Y so it
     * reads as lying on the ground plane rather than facing the camera — the
     * difference between a landing and an explosion.
     */
    function shockwave(target, { strength = 1 } = {}) {
        if (reducedMotion()) return;
        const rect = resolveRect(target);
        if (!rect || !(rect.width > 0)) return;
        const el = spawn('fx-shockwave');
        const width = Math.max(uiPx(34), rect.width * 1.35) * strength;
        el.style.left = `${rect.left + rect.width / 2}px`;
        // Centred just BELOW the card's foot, so the ring travels out across
        // the table rather than across the card's own text panel.
        el.style.top = `${rect.top + rect.height * 1.06}px`;
        el.style.width = `${width}px`;
        el.style.height = `${width * 0.34}px`;
        runAnimation(el, [
            { transform: 'translate(-50%, -50%) scale(0.24)', opacity: 0 },
            { transform: 'translate(-50%, -50%) scale(0.58)', opacity: 0.95, offset: 0.18 },
            { transform: 'translate(-50%, -50%) scale(1)', opacity: 0 },
        ], { duration: 460, easing: 'cubic-bezier(0.15, 0.85, 0.3, 1)' }, 820, 'fx-overlay-fallback');
    }

    /**
     * The impact frame, drawn at the CONTACT LINE — a bar of blown-out light
     * along the card's foot, where it met the board.
     *
     * Deliberately not a sheet over the whole card: a screen-blended pane
     * across the face bleaches the name and text panel, which reads as a
     * rendering fault rather than as light. It lives for three frames.
     */
    function contactFlash(rect, { strength = 0.95 } = {}) {
        if (!rect || reducedMotion()) return;
        const el = spawn('fx-flash');
        const height = Math.max(uiPx(10), rect.height * 0.17);
        el.style.left = `${rect.left + rect.width / 2}px`;
        el.style.top = `${rect.top + rect.height}px`;
        el.style.width = `${rect.width * 1.5}px`;
        el.style.height = `${height}px`;
        runAnimation(el, [
            { transform: 'translate(-50%, -50%) scaleX(0.55)', opacity: 0 },
            { transform: 'translate(-50%, -50%) scaleX(1)', opacity: strength, offset: 0.28 },
            { transform: 'translate(-50%, -50%) scaleX(1.06)', opacity: strength, offset: 0.6 },
            { transform: 'translate(-50%, -50%) scaleX(1.14)', opacity: 0 },
        ], { duration: CONTACT_FLASH_MS, easing: 'linear' }, CONTACT_FLASH_MS + 300, 'fx-overlay-fallback');
    }

    /**
     * The cards flanking a slot, nearest first, two deep per side. The near
     * pair takes the shove; the far pair follows late and softer, so the lane
     * reads as a wave travelling out of the impact rather than everything
     * twitching at once.
     */
    function boardNeighbors(cardElement) {
        const slot = cardElement?.closest?.('.card-slot');
        const container = slot?.parentElement;
        if (!slot || !container) return [];
        const slots = [...container.children];
        const index = slots.indexOf(slot);
        if (index < 0) return [];
        return [-1, 1, -2, 2]
            .map((offset) => {
                const i = index + offset;
                const el = i >= 0 ? slots[i]?.querySelector?.('.card') : null;
                return el ? { el, away: offset < 0 ? -1 : 1, distance: Math.abs(offset) } : null;
            })
            .filter(Boolean);
    }

    /**
     * A neighbour shoved aside by something that just hit the board — the
     * REAL card, moved, with a spring back. An overlay sheet fading over a
     * card that never moves is not secondary motion; it is a decal.
     */
    function neighborPush({ el, away, distance }) {
        const rect = resolveRect(el);
        if (!rect || !(rect.width > 0)) return;
        const reach = clamp(uiPx(6), rect.width * 0.1, uiPx(10)) * (distance === 1 ? 1 : 0.5);
        const push = (reach * away).toFixed(1);
        const back = (-reach * away * 0.3).toFixed(1);
        moveCard(el, [
            { transform: 'translate(0px, 0px)', easing: 'cubic-bezier(0.15, 0.85, 0.3, 1)' },
            { transform: `translate(${push}px, ${(rect.height * 0.012).toFixed(1)}px)`, offset: 0.24, easing: 'ease-in-out' },
            { transform: `translate(${back}px, 0px)`, offset: 0.58, easing: SETTLE_EASING },
            { transform: 'translate(0px, 0px)' },
        ], { duration: 440, delay: distance === 1 ? 0 : 70, easing: 'linear' });
    }

    // A directional contact flare that sits over a struck card, portrait, or
    // deck. The live raster is deliberately never transformed or filtered:
    // moving a scaled image layer forces browsers to resample it on each frame,
    // which presents as flicker/aliasing on dense tablet displays.
    function impactKick(target, dirX, dirY, strength) {
        const rect = resolveRect(target);
        if (!rect) return;
        const knock = uiPx(Math.max(8, Math.min(22, 14 * strength)));
        const magnitude = Math.hypot(dirX, dirY) || 1;
        const kx = (dirX / magnitude) * knock;
        const ky = (dirY / magnitude) * knock;
        const el = spawn('fx-impact-kick');
        el.style.left = `${rect.left + rect.width / 2}px`;
        el.style.top = `${rect.top + rect.height / 2}px`;
        el.style.width = `${rect.width}px`;
        el.style.height = `${rect.height}px`;
        el.style.setProperty('--fx-kick-x', `${kx}px`);
        el.style.setProperty('--fx-kick-y', `${ky}px`);
        el.style.setProperty('--fx-kick-back-x', `${-kx * 0.28}px`);
        el.style.setProperty('--fx-kick-back-y', `${-ky * 0.28}px`);
        runAnimation(el, [
            { transform: 'translate(-50%, -50%) scale(0.92)', opacity: 0, easing: 'ease-out' },
            { transform: `translate(calc(-50% + ${kx}px), calc(-50% + ${ky}px)) scale(1.06)`, opacity: 0.95, offset: 0.18, easing: 'ease-in' },
            { transform: `translate(calc(-50% - ${kx * 0.28}px), calc(-50% - ${ky * 0.28}px)) scale(1.02)`, opacity: 0.5, offset: 0.48, easing: 'ease-out' },
            { transform: 'translate(-50%, -50%) scale(1)', opacity: 0 },
        ], { duration: 420, easing: 'linear' }, 820, 'fx-overlay-fallback');
    }

    /**
     * "The hit connects." The contact pop, fired on the contact frame of a
     * strike by either player: a strong ring + a delayed shock ring, a spark
     * burst, and a directional contact flare moving away from the blow. All of
     * it is disposable overlay geometry, so it is safe on a portrait or a deck
     * pile, whose live rasters are contractually never transformed; a struck
     * board CARD additionally gets moved by `knockBack`.
     *
     * Deliberately no screen shake — this animates a pre-resolution action
     * (which fires for non-damaging abilities too), so the heavy screen jolt
     * stays with the authoritative damage resolution seconds later. Reduced
     * motion collapses it to the ring alone; the caller still plays the hit
     * sound, so the strike is never fully silent.
     */
    function impactReaction(targetEl, { dirX = 0, dirY = 0, strength = 1 } = {}) {
        if (!targetEl) return;
        impactAt(targetEl, { strength: 1.5 * strength });
        if (reducedMotion()) return;
        setTimeout(() => impactAt(targetEl, { strength: 0.8 * strength }), 90);
        burst(targetEl, { count: 8, radiusX: uiPx(34) * strength, radiusY: uiPx(26) * strength });
        impactKick(targetEl, dirX, dirY, strength);
    }

    /**
     * A struck board card thrown off its footing — the real card, moved along
     * the blow's direction, then springing back past rest.
     *
     * Only board cards have a mover; a portrait or a deck pile is left to
     * `impactReaction`'s overlays, because their live rasters are contractually
     * never transformed (see `docs/architecture-client.md`).
     */
    function knockBack(targetEl, dirX, dirY, strength = 1) {
        if (reducedMotion()) return;
        const magnitude = Math.hypot(dirX, dirY);
        // No attacker on screen means no line to be thrown along. The ring and
        // the sound still land; inventing a direction would be fiction.
        if (!(magnitude > 0)) return;
        const rect = resolveRect(targetEl);
        if (!rect || !(rect.width > 0)) return;
        const knock = clamp(uiPx(6), rect.width * 0.15 * strength, uiPx(20));
        const kx = ((dirX / magnitude) * knock).toFixed(1);
        const ky = ((dirY / magnitude) * knock).toFixed(1);
        const bx = ((-dirX / magnitude) * knock * 0.3).toFixed(1);
        const by = ((-dirY / magnitude) * knock * 0.3).toFixed(1);
        moveCard(targetEl, [
            { transform: 'translate(0px, 0px)', easing: 'cubic-bezier(0.1, 0.9, 0.2, 1)' },
            { transform: `translate(${kx}px, ${ky}px)`, offset: 0.16, easing: 'ease-out' },
            { transform: `translate(${bx}px, ${by}px)`, offset: 0.5, easing: SETTLE_EASING },
            { transform: 'translate(0px, 0px)' },
        ], { duration: 400, easing: 'linear' });
    }

    /**
     * How hard a strike reads. Presentation observing authoritative data, in
     * the order it becomes trustworthy:
     *  - `damage`, when the caller already holds a resolved number;
     *  - otherwise the attacker's own authoritative `manaCost` — its mass.
     *
     * `characterActionStarted` fires BEFORE the storyteller resolves, so the
     * damage of the strike being animated is genuinely unknowable client-side
     * at that moment. Mana cost is the honest stand-in; it is never inferred,
     * guessed, or back-filled from a later event.
     */
    function actionWeight({ damage = null, manaCost = null } = {}) {
        if (typeof damage === 'number' && damage > 0) return clamp(0.8, 0.68 + damage * 0.15, 1.7);
        if (typeof manaCost === 'number' && manaCost > 0) return clamp(0.8, 0.74 + manaCost * 0.09, 1.5);
        return 1;
    }

    // ---------- Shakes ----------

    function screenShake(damage) {
        const params = shakeParams(damage);
        if (!params || reducedMotion()) return;
        if (!document.getElementById('game-view')) return;
        const amp = uiPx(params.amp);

        if (activeScreenImpact?.isConnected) activeScreenImpact.remove();
        const el = spawn('fx-screen-impact');
        activeScreenImpact = el;
        el.style.setProperty('--fx-impact-amp', `${amp}px`);
        el.style.setProperty('--fx-impact-amp-neg', `${-amp}px`);
        el.style.setProperty('--fx-impact-half', `${amp * 0.5}px`);
        el.style.setProperty('--fx-impact-half-neg', `${-amp * 0.5}px`);
        el.style.setProperty('--fx-duration', `${params.duration}ms`);
        const frames = SHAKE_PATTERN.map(([x, y], index) => ({
            transform: `translate(${x * amp}px, ${y * amp}px)`,
            opacity: index === 0 || index === SHAKE_PATTERN.length - 1
                ? 0
                : Math.max(0.16, 0.62 - index * 0.045),
        }));
        runAnimation(el, frames, {
            duration: params.duration,
            easing: 'linear',
        }, params.duration + 400, 'fx-overlay-fallback');
    }

    function portraitShake(target, amount) {
        if (typeof amount !== 'number' || !(amount > 0) || reducedMotion()) return;
        let el = null;
        if (target === 'player') el = document.getElementById('player-portrait');
        else if (target === 'opponent') el = document.getElementById('opponent-portrait');
        else if (target instanceof HTMLElement) el = target;
        if (!el) return;

        const amp = Math.max(4, Math.min(16, 2 + amount * 0.9));
        const ampPx = uiPx(amp);
        const duration = 500;

        const rect = resolveRect(el);
        if (!rect) return;
        const previous = activePortraitImpacts.get(el);
        if (previous?.isConnected) previous.remove();

        const impact = spawn('fx-portrait-impact');
        activePortraitImpacts.set(el, impact);
        impact.style.left = `${rect.left + rect.width / 2}px`;
        impact.style.top = `${rect.top + rect.height / 2}px`;
        impact.style.width = `${rect.width}px`;
        impact.style.height = `${rect.height}px`;
        impact.style.setProperty('--fx-portrait-amp', `${ampPx}px`);
        impact.style.setProperty('--fx-portrait-amp-neg', `${-ampPx}px`);
        impact.style.setProperty('--fx-portrait-half', `${ampPx * 0.5}px`);
        impact.style.setProperty('--fx-portrait-half-neg', `${-ampPx * 0.5}px`);
        impact.style.setProperty('--fx-duration', `${duration}ms`);
        const frames = SHAKE_PATTERN.map(([x, y], index) => ({
            transform: `translate(calc(-50% + ${x * ampPx}px), calc(-50% + ${y * ampPx}px)) rotate(${(index % 2 ? 1 : -1) * Math.min(1.6, amp * 0.12)}deg)`,
            opacity: index === 0 || index === SHAKE_PATTERN.length - 1
                ? 0
                : Math.max(0.18, 0.92 - index * 0.08),
        }));
        frames[0] = { transform: 'translate(-50%, -50%) rotate(0deg)', opacity: 0 };
        frames[frames.length - 1] = { transform: 'translate(-50%, -50%) rotate(0deg)', opacity: 0 };
        runAnimation(impact, frames, { duration, easing: 'linear' }, duration + 400, 'fx-overlay-fallback');
    }

    // ---------- Board effect capture & playback ----------

    function sanitizeClone(el) {
        const clone = el.cloneNode(true);
        Cards?.copyCanvasPaints?.(el, clone);
        for (const node of [clone, ...clone.querySelectorAll('*')]) {
            node.removeAttribute('id');
            node.removeAttribute('data-instance-id');
            node.removeAttribute('data-zone');
            // A clone is a SNAPSHOT of what is on screen right now, so it must
            // never re-arm the art-grace/cohort withhold: those rules restart
            // from zero on a fresh node and would blank the effect for 450ms
            // (an empty slot with sparkles where the dying card should be).
            node.classList?.remove('card-art-loading', 'card-cohort-hold');
        }
        for (const video of [...clone.querySelectorAll('video')]) video.remove();
        return clone;
    }

    function captureRects(plan) {
        if (!plan) return;
        plan.captures = [];
        for (const side of ['player', 'opponent']) {
            for (const card of plan[side]?.deaths || []) {
                const el = findBoardCard(card.instanceId);
                if (!el) continue;
                const rect = el.getBoundingClientRect();
                if (!rect || !rect.width) continue;
                plan.captures.push({ card, rect, clone: sanitizeClone(el) });
            }
        }
    }

    const polygonOf = (points) => `polygon(${points
        .map((p) => `${p.x.toFixed(1)}% ${p.y.toFixed(1)}%`).join(', ')})`;

    /**
     * The fracture this body breaks along, in percentages of the card: a main
     * seam running top to bottom, and one branch running off it to the right
     * edge. Generated per death, so no two bodies break the same way.
     *
     * This is the single source of truth for BOTH the drawn crack and the
     * pieces the card comes apart into — a seam that is drawn and then healed
     * reads as a decal, so the break has to happen along the line the player
     * just watched open.
     */
    function generateSeam() {
        const steps = 6;
        const seam = [];
        for (let i = 0; i <= steps; i += 1) {
            seam.push({ x: 50 + (Math.random() - 0.5) * 13, y: (i / steps) * 100 });
        }
        const branchIndex = 3 + Math.round(Math.random());
        const start = seam[branchIndex];
        const branch = [start];
        for (let i = 1; i <= 3; i += 1) {
            branch.push({
                x: start.x + (100 - start.x) * (i / 3),
                y: start.y + (Math.random() - 0.5) * 9,
            });
        }
        branch[branch.length - 1].x = 100;
        return { seam, branch, branchIndex };
    }

    /** The three regions the seam and its branch cut the card into. */
    function seamPieces({ seam, branch, branchIndex }) {
        return [
            {
                points: [{ x: 0, y: 0 }, ...seam, { x: 0, y: 100 }],
                origin: '32% 58%',
                away: -1,
            },
            {
                points: [...seam.slice(0, branchIndex + 1), ...branch.slice(1), { x: 100, y: 0 }],
                origin: '72% 26%',
                away: 1,
            },
            {
                points: [...branch.slice().reverse(), ...seam.slice(branchIndex + 1), { x: 100, y: 100 }],
                origin: '72% 82%',
                away: 1,
            },
        ];
    }

    /** A thin band of hot light along one fracture line, opening then gone. */
    function drawCrack(rect, points, { vertical = true } = {}) {
        const el = spawn('fx-crack');
        placeOver(el, rect);
        const width = 1.7;
        const forward = points.map((p) => (vertical
            ? { x: p.x - width, y: p.y }
            : { x: p.x, y: p.y - width }));
        const back = points.slice().reverse().map((p) => (vertical
            ? { x: p.x + width, y: p.y }
            : { x: p.x, y: p.y + width }));
        el.style.clipPath = polygonOf([...forward, ...back]);
        runAnimation(el, [
            { opacity: 0, transform: 'scale(0.94)' },
            { opacity: 1, transform: 'scale(1)', offset: 0.2 },
            { opacity: 0.9, transform: 'scale(1.03)', offset: 0.62 },
            // It does not fade out on its own — it hands over to the break.
            { opacity: 0.5, transform: 'scale(1.05)' },
        ], { duration: DEATH_BODY_MS, easing: 'ease-out' }, DEATH_BODY_MS + 60, 'fx-overlay-fallback');
    }

    /**
     * The body coming apart. Each piece is the same captured card clipped to
     * its own region of the seam, falling on its own gravity arc with its own
     * spin about its own centre of mass — and the pieces, not the intact body,
     * carry the fade out.
     *
     * Every piece stays legible until it is gone: the colour cools but never
     * drops below brightness 0.7, because a card the player cannot identify at
     * the moment it dies has failed at the one job the exit has.
     */
    function breakBody(rect, pieces, copyContent) {
        pieces.forEach((piece, index) => {
            const el = spawn('fx-death-piece');
            placeOver(el, rect);
            el.style.clipPath = polygonOf(piece.points);
            el.style.transformOrigin = piece.origin;
            const content = copyContent();
            if (content) el.appendChild(content);

            const drift = piece.away * rect.width * (0.17 + index * 0.06);
            const spin = piece.away * (10 + index * 7);
            const lift = -rect.height * (0.05 + 0.03 * (index % 2));
            const drop = rect.height * (0.52 + 0.15 * index);
            runAnimation(el, [
                { transform: 'translate(0px, 0px) rotate(0deg)', opacity: 1, filter: 'brightness(0.98) saturate(0.85)', easing: 'ease-out' },
                { transform: `translate(${(drift * 0.32).toFixed(1)}px, ${lift.toFixed(1)}px) rotate(${(spin * 0.28).toFixed(1)}deg)`, opacity: 1, filter: 'brightness(0.93) saturate(0.8)', offset: 0.22, easing: 'ease-in' },
                { transform: `translate(${(drift * 0.78).toFixed(1)}px, ${(drop * 0.44).toFixed(1)}px) rotate(${(spin * 0.72).toFixed(1)}deg)`, opacity: 0.86, filter: 'brightness(0.84) saturate(0.72)', offset: 0.62, easing: 'ease-in' },
                { transform: `translate(${drift.toFixed(1)}px, ${drop.toFixed(1)}px) rotate(${spin.toFixed(1)}deg)`, opacity: 0, filter: 'brightness(0.7) saturate(0.6)' },
            ], {
                duration: DEATH_PIECES_MS,
                easing: 'linear',
                delay: index * 26,
            }, DEATH_PIECES_MS + 400, 'fx-overlay-fallback');
        });
    }

    /**
     * A DEATH, in two beats. First the BODY takes the blow — a three-frame
     * blow-out, a jolt off the board, a rattle — while the fracture opens
     * along a seam generated for this death. Then, on that seam, the body
     * BREAKS: three pieces fall on their own arcs, spinning about their own
     * mass, and they carry the fade out.
     *
     * The intact card never slides away whole. A crack that opens and then
     * heals is a decal; the promise the seam makes is paid by the break.
     *
     * Everything stays LEGIBLE until it is gone — the colour cools but never
     * drains to a silhouette, because a card the player cannot identify at the
     * moment it dies has failed at the one job the exit has. A shadow pools
     * and collapses beneath it, and the break throws ash.
     */
    function playGhost(capture) {
        const el = spawn('fx-ghost');
        placeOver(el, capture.rect);
        el.appendChild(capture.clone);

        if (reducedMotion()) {
            runAnimation(el, [{ opacity: 1 }, { opacity: 0 }], { duration: 200, easing: 'ease-out' }, 500);
            return;
        }

        const shadow = spawn('fx-death-shadow');
        shadow.style.left = `${capture.rect.left + capture.rect.width / 2}px`;
        shadow.style.top = `${capture.rect.top + capture.rect.height * 0.95}px`;
        shadow.style.width = `${capture.rect.width * 0.9}px`;
        shadow.style.height = `${capture.rect.width * 0.3}px`;
        runAnimation(shadow, [
            { transform: 'translate(-50%, -50%) scale(0.5, 0.7)', opacity: 0 },
            { transform: 'translate(-50%, -50%) scale(1.05, 1)', opacity: 0.7, offset: 0.3 },
            { transform: 'translate(-50%, -50%) scale(0.3, 0.5)', opacity: 0 },
        ], { duration: DEATH_MS, easing: 'ease-out' }, DEATH_MS + 300, 'fx-overlay-fallback');

        // Offsets 0.06–0.18 of 260ms ≈ 16–47ms: the blow-out is three frames,
        // and it peaks at 2.15 rather than a value that erases the card's own
        // face. The body ends this beat back at REST, so the pieces that take
        // over start from a pose identical to the one it left.
        const u = uiPx(1);
        const shake = (n) => `${((n % 2 ? 1 : -1) * (3.2 - n * 0.5) * u).toFixed(1)}px`;
        runAnimation(el, [
            { transform: 'translate(0px, 0px) scale(1, 1) rotate(0deg)', filter: 'brightness(1) saturate(1)', easing: 'linear' },
            { transform: `translate(${shake(1)}, ${(-3 * u).toFixed(1)}px) scale(1.05, 1.05) rotate(0.8deg)`, filter: 'brightness(2.15) saturate(0.5)', offset: 0.06, easing: 'linear' },
            { transform: `translate(${shake(2)}, ${(-2 * u).toFixed(1)}px) scale(1.04, 1.04) rotate(-0.9deg)`, filter: 'brightness(1.95) saturate(0.55)', offset: 0.18, easing: 'linear' },
            // The jolt rattles out...
            { transform: `translate(${shake(3)}, ${u.toFixed(1)}px) scale(1.01, 0.98) rotate(-1.8deg)`, filter: 'brightness(1.15) saturate(0.8)', offset: 0.28, easing: 'ease-out' },
            { transform: `translate(${shake(4)}, ${(3 * u).toFixed(1)}px) scale(1, 0.99) rotate(-2.4deg)`, filter: 'brightness(1.05) saturate(0.9)', offset: 0.44, easing: 'ease-in-out' },
            { transform: `translate(${(-u).toFixed(1)}px, ${(2 * u).toFixed(1)}px) scale(1, 0.995) rotate(-1.1deg)`, filter: 'brightness(1) saturate(0.9)', offset: 0.68, easing: 'ease-out' },
            // ...and it HANGS, whole, while the seam finishes opening.
            { transform: 'translate(0px, 0px) scale(1, 1) rotate(0deg)', filter: 'brightness(0.98) saturate(0.85)' },
        ], { duration: DEATH_BODY_MS, easing: 'linear' }, DEATH_BODY_MS + 80);

        const fracture = generateSeam();
        drawCrack(capture.rect, fracture.seam, { vertical: true });
        drawCrack(capture.rect, fracture.branch, { vertical: false });

        // The pieces are spawned a frame BEFORE the body leaves. Their opening
        // pose is pixel-identical to its closing one, so the overlap is
        // invisible where a gap would flicker.
        const source = capture.clone;
        setTimeout(() => {
            breakBody(capture.rect, seamPieces(fracture), () => {
                const copy = source.cloneNode(true);
                Cards?.copyCanvasPaints?.(source, copy);
                return copy;
            });
            debris(capture.rect, { count: 11, palette: 'ash', origin: 'center', power: 1.15 });
        }, Math.max(0, DEATH_BODY_MS - 24));
    }

    /**
     * The attacker's own body, thrown at its target and pulled home. ONE body:
     * the real card leaves its slot, so the wind-up is drawn by the thing that
     * is about to strike rather than by a copy overlapping it, and no frame
     * ever shows the attacker twice.
     *
     * @returns {{contactMs: number}|null} null when there is nothing to move.
     */
    function lungeCard(sourceEl, from, to, duration, weight) {
        const dx = (to.left + to.width / 2) - (from.left + from.width / 2);
        const dy = (to.top + to.height / 2) - (from.top + from.height / 2);
        const dist = Math.hypot(dx, dy) || 1;
        const ux = dx / dist;
        const uy = dy / dist;
        // Heavier bodies coil further back and overshoot harder coming home.
        const coil = uiPx(16 + 10 * weight);
        const bank = clamp(-16, ux * 16 * weight, 16);
        const anim = moveCard(sourceEl, [
            { transform: 'translate(0px, 0px) scale(1, 1) rotate(0deg)', easing: 'ease-out' },
            // Anticipation: back off the line of attack, coiling narrow+tall...
            { transform: `translate(${(-ux * coil).toFixed(1)}px, ${(-uy * coil).toFixed(1)}px) scale(0.93, 1.06) rotate(${(-bank * 0.5).toFixed(1)}deg)`, offset: 0.2, easing: 'linear' },
            // ...and HANG there, so the release reads as released.
            { transform: `translate(${(-ux * coil * 1.08).toFixed(1)}px, ${(-uy * coil * 1.08).toFixed(1)}px) scale(0.92, 1.07) rotate(${(-bank * 0.56).toFixed(1)}deg)`, offset: 0.3, easing: STRIKE_EASING },
            { transform: `translate(${(dx * 0.92).toFixed(1)}px, ${(dy * 0.92).toFixed(1)}px) scale(1.15, 0.9) rotate(${bank.toFixed(1)}deg)`, offset: 0.46, easing: 'ease-out' },
            // CONTACT.
            { transform: `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px) scale(1.11, 0.94) rotate(${(bank * 0.7).toFixed(1)}deg)`, offset: 0.5, easing: 'ease-out' },
            { transform: `translate(${(dx * 0.58).toFixed(1)}px, ${(dy * 0.58).toFixed(1)}px) scale(0.95, 1.06) rotate(${(bank * 0.2).toFixed(1)}deg)`, offset: 0.63, easing: 'ease-in-out' },
            // Home the long way: past rest, then settling onto it.
            { transform: `translate(${(-ux * uiPx(12) * weight).toFixed(1)}px, ${(-uy * uiPx(12) * weight).toFixed(1)}px) scale(1.02, 0.98) rotate(${(-bank * 0.25).toFixed(1)}deg)`, offset: 0.81, easing: SETTLE_EASING },
            { transform: 'translate(0px, 0px) scale(1, 1) rotate(0deg)' },
        ], { duration, easing: 'linear' });
        return anim ? { contactMs: Math.round(duration * 0.5) } : null;
    }

    /**
     * The same body, carried to an ALLY: a step in, a touch, a step back.
     *
     * No coil and no squash — those are the vocabulary of a blow — but not a
     * `div` sliding about either: it breathes in before it goes, leans into
     * the reach, lifts a hair as it arrives, HOLDS on the ally for a beat (a
     * touch lingers where a blow rebounds) and settles home without the
     * strike's overshoot. It stops short of covering the ally: edges deep in
     * contact, never a body-check.
     */
    function reachCard(sourceEl, from, to, duration, weight) {
        const dx = (to.left + to.width / 2) - (from.left + from.width / 2);
        const dy = (to.top + to.height / 2) - (from.top + from.height / 2);
        const dist = Math.hypot(dx, dy) || 1;
        const ux = dx / dist;
        const uy = dy / dist;
        const reach = clamp(0.62, 1 - (Math.min(from.width, to.width) * 0.55) / dist, 0.9);
        const bank = clamp(-9, ux * 9 * weight, 9);
        const anim = moveCard(sourceEl, [
            { transform: 'translate(0px, 0px) scale(1) rotate(0deg)', easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
            // A breath in, not a wind-up.
            { transform: `translate(${(-ux * uiPx(5)).toFixed(1)}px, ${(-uy * uiPx(5) - uiPx(3)).toFixed(1)}px) scale(1.02) rotate(${(-bank * 0.3).toFixed(1)}deg)`, offset: 0.18, easing: 'cubic-bezier(0.3, 0, 0.1, 1)' },
            // The reach, leaning into it.
            { transform: `translate(${(dx * reach * 0.96).toFixed(1)}px, ${(dy * reach * 0.96 - uiPx(4)).toFixed(1)}px) scale(1.04) rotate(${bank.toFixed(1)}deg)`, offset: 0.46, easing: 'ease-out' },
            // CONTACT — and the hold on it.
            { transform: `translate(${(dx * reach).toFixed(1)}px, ${(dy * reach - uiPx(3)).toFixed(1)}px) scale(1.03) rotate(${(bank * 0.8).toFixed(1)}deg)`, offset: 0.5, easing: 'linear' },
            { transform: `translate(${(dx * reach * 0.98).toFixed(1)}px, ${(dy * reach * 0.98 - uiPx(2)).toFixed(1)}px) scale(1.03) rotate(${(bank * 0.7).toFixed(1)}deg)`, offset: 0.64, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' },
            // Home, settling without a recoil's overshoot.
            { transform: `translate(${(-ux * uiPx(2)).toFixed(1)}px, ${(-uy * uiPx(2)).toFixed(1)}px) scale(1) rotate(${(-bank * 0.08).toFixed(1)}deg)`, offset: 0.9, easing: SETTLE_EASING },
            { transform: 'translate(0px, 0px) scale(1) rotate(0deg)' },
        ], { duration, easing: 'linear' });
        return anim ? { contactMs: Math.round(duration * 0.5) } : null;
    }

    /**
     * A board attack, for BOTH players, played on the AUTHORITATIVE accept
     * rather than at pointer release: the server has validated the action and
     * is resolving it, so this observes a decision instead of predicting one.
     *
     * Nothing about the target moves before contact. The ring, the kick, the
     * knock-back and the single `impact` sound are all scheduled on the same
     * beat — a target that flinches while the attacker is still winding up
     * reads as the game telling you the outcome early.
     *
     * `friendly` — the target is on the ACTOR's own side. The body still goes
     * there in person (a reach, not a lunge), but the contact is the ally
     * beat: no ring, no kick, no knock-back, no `impact` cue. A Character
     * acting on its ally is not hitting it, and what it did is for the
     * storyteller to say. ("make targeting of friendly targets with a card
     * have a more positive looking anim ... keep it ambiguous".)
     *
     * @returns {{contactMs: number, moved: boolean}|null} null when the target
     *   cannot be resolved — there is no strike to show, and the caller must
     *   not fake one.
     */
    function lungeAttack({ sourceEl, targetEl, duration = ATTACK_LUNGE_MS, damage = null, manaCost = null, friendly = false } = {}) {
        if (!targetEl) return null;
        const to = resolveRect(targetEl);
        if (!to || !(to.width > 0)) return null;
        const targetInstanceId = targetEl.closest?.('.board-container')
            ? targetEl.dataset?.instanceId
            : null;

        const weight = actionWeight({ damage, manaCost });
        const from = resolveRect(sourceEl);
        const travelMs = Math.round(duration * (0.86 + weight * 0.14));
        const carry = friendly ? reachCard : lungeCard;
        const travel = (!reducedMotion() && from && from.width > 0)
            ? carry(sourceEl, from, to, travelMs, weight)
            : null;
        // Reduced motion (or an attacker no longer on the board) still lands
        // the hit — on the target, where it happened, never as sound alone.
        const contactMs = travel ? travel.contactMs : 0;
        const dir = unitDirection(from, to);
        // A state update may replace both lanes during the wind-up. Follow a
        // board target by authoritative instance id at contact; never knock a
        // detached predecessor or an art-pending replacement. The accepted
        // action still lands visibly at its captured contact geometry if the
        // live body disappeared or is being withheld.
        const liveTargetAtContact = () => {
            const candidate = targetInstanceId ? findBoardCard(targetInstanceId) : targetEl;
            return candidate?.isConnected
                && !candidate.classList?.contains('card-summon-art-pending')
                && (!targetInstanceId || candidate.closest?.('.board-container'))
                ? candidate
                : null;
        };
        if (friendly) {
            // The gather is the anticipation, so it opens BEFORE the body
            // arrives and the bloom lands on the contact frame. The ally does
            // not react before that frame any more than a defender does.
            setTimeout(() => {
                touchAt(liveTargetAtContact() || to, { strength: weight });
            }, Math.max(0, contactMs - TOUCH_BLOOM_AT_MS));
            // Still a sound on the contact frame — silence on the moment reads
            // as a bug — but the ordinary magic cue, not a blow.
            setTimeout(() => SoundEffects?.play?.('magic'), contactMs);
            return { contactMs, moved: !!travel };
        }
        setTimeout(() => {
            const liveTarget = liveTargetAtContact();
            impactReaction(liveTarget || to, { dirX: dir.x, dirY: dir.y, strength: weight });
            if (liveTarget) knockBack(liveTarget, dir.x, dir.y, weight);
            SoundEffects?.play?.('impact');
        }, contactMs);
        return { contactMs, moved: !!travel };
    }

    /**
     * A card thrown across the battlefield, leaving a zone it no longer
     * occupies — an enemy summon lifting out of their hand, an enemy spell
     * flying at its target. It reads as a real, weighty throw: a wind-up, a
     * powered arc that banks into its trajectory (squash/stretch + a
     * motion-trail streak), then a settle. Modes:
     *  - holdAtEnd: land with an overshoot and stay pulsing until its owner
     *               completes the authoritative handoff or clears it,
     *  - default:   glide to the target and dissolve (an enemy spell).
     *
     * A board ATTACK is deliberately NOT built on this: the attacker still
     * owns its slot, so it is moved in person by `lungeAttack` instead of
     * being impersonated by a clone that overlaps it.
     *
     * The element is parked at its journey's END and animated FLIP-style from
     * an inverse offset, so no WAAPI fill mode is needed and the jsdom/no-WAAPI
     * fallback degrades to "appears at the destination".
     *
     * @returns {{el: Element, contactMs: number}|null} contactMs is when the
     *   traveler visually touches the target (schedule impacts off it).
     */
    function glidePlay({ sourceEl = null, contentEl = null, from, to, duration = 500, holdAtEnd = false, trail = false, ownedLifecycle = false } = {}) {
        if (reducedMotion()) return null;
        const fromRect = resolveRect(from);
        const toRect = resolveRect(to);
        if (!fromRect || !toRect || !(fromRect.width > 0) || !(toRect.width > 0)) return null;
        const content = contentEl || (sourceEl ? sanitizeClone(sourceEl) : null);
        if (!content) return null;

        const el = spawn('fx-glide');
        if (holdAtEnd) el.dataset.fxPersist = '1';
        /* An OWNED clone's teardown belongs to its caller (EnemyReplay hides
           the authoritative card and removes the traveler itself once travel
           and board presentation both settle). The snapshot sweep below must
           not delete it mid-flight — that was the enemy summon's freeze. The
           stale sweep still applies as a leak backstop. */
        if (ownedLifecycle) el.dataset.fxOwned = '1';
        if (trail) el.classList.add('fx-glide--trail');
        el.appendChild(content);

        el.style.left = `${toRect.left}px`;
        el.style.top = `${toRect.top}px`;
        el.style.width = `${fromRect.width}px`;
        el.style.height = `${fromRect.height}px`;

        const dx = (toRect.left + toRect.width / 2) - (fromRect.left + fromRect.width / 2);
        const dy = (toRect.top + toRect.height / 2) - (fromRect.top + fromRect.height / 2);
        const dist = Math.hypot(dx, dy) || 1;
        // Bank into the throw's horizontal lean; the streak only shows when asked.
        const bank = Math.max(-16, Math.min(16, (dx / dist) * 18));
        const contactMs = Math.round(duration * 0.85);

        if (canAnimate(el)) {
            // Easing lives on the keyframes — options-level easing warps the
            // whole iteration timeline (the wave-1 turn-banner lesson).
            const arrival = holdAtEnd
                ? [
                    { transform: `translate(${-dx * 0.12}px, ${-dy * 0.12}px) scale(1.16) rotate(${bank * 0.4}deg)`, opacity: 1, offset: 0.7, easing: 'ease-out' },
                    { transform: `translate(${uiPx(2)}px, ${uiPx(-3)}px) scale(0.97) rotate(0deg)`, opacity: 1, offset: 0.85, easing: 'ease-in-out' },
                    { transform: 'translate(0px, 0px) scale(1) rotate(0deg)', opacity: 1 },
                ]
                : [
                    { transform: `translate(${-dx * 0.14}px, ${-dy * 0.14}px) scale(1.08) rotate(${bank * 0.5}deg)`, opacity: 1, offset: 0.72, easing: 'ease-out' },
                    { transform: 'translate(0px, 0px) scale(1) rotate(0deg)', opacity: 0 },
                ];
            const frames = [
                { transform: `translate(${-dx}px, ${-dy}px) scale(1) rotate(0deg)`, opacity: 1, easing: POP_EASING },
                { transform: `translate(${-dx * 0.86}px, ${-dy * 0.86}px) scale(1.12) rotate(${bank}deg)`, opacity: 1, offset: 0.22, easing: 'ease-in-out' },
                ...arrival,
            ];
            try {
                const anim = el.animate(frames, { duration, easing: 'linear' });
                anim.finished
                    .then(() => {
                        if (!el.isConnected) return;
                        if (holdAtEnd) el.classList.add('fx-glide--landed');
                        else el.remove();
                    })
                    .catch(() => { /* cancelled — eviction/sweep owns removal */ });
            } catch (_) { /* timed paths below still clean up */ }
        }
        if (holdAtEnd) {
            setTimeout(() => { if (el.isConnected) el.classList.add('fx-glide--landed'); }, duration);
        } else {
            scheduleRemoval(el, duration + 400);
        }
        return { el, contactMs };
    }

    // Active number tickers, so a re-trigger supersedes cleanly.
    const activeCounts = new WeakMap();

    /**
     * Tick an element's text from one number to another (ease-out). The
     * shared primitive behind health/mana/deck and stardust displays.
     * Reduced motion (or a missing/equal start) snaps instantly.
     */
    function animateCount(el, from, to, { duration = 500, render } = {}) {
        if (!el) return;
        const target = Number(to);
        if (!Number.isFinite(target)) return;
        const fmt = typeof render === 'function' ? render : String;

        const prev = activeCounts.get(el);
        if (prev) prev.cancelled = true;

        const start = Number.isFinite(Number(from)) ? Number(from) : target;
        if (reducedMotion() || start === target || !(duration > 0)) {
            el.textContent = fmt(target);
            return;
        }

        const state = { cancelled: false, target };
        activeCounts.set(el, state);
        const raf = typeof window.requestAnimationFrame === 'function'
            ? window.requestAnimationFrame.bind(window)
            : (cb) => setTimeout(cb, 16);
        const t0 = Date.now();
        const tick = () => {
            if (state.cancelled || !el.isConnected) return;
            const t = Math.min(1, (Date.now() - t0) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            el.textContent = fmt(Math.round(start + (target - start) * eased));
            if (t < 1) raf(tick);
            else activeCounts.delete(el);
        };
        raf(tick);
    }

    /**
     * Ticked number display: remembers the previous value on the element
     * (data-count-value), ticks changes via animateCount with a count-pulse
     * glow, and sets text directly on first paint. Used by the stardust
     * balances; the battle indicators keep an equivalent local helper.
     */
    function setCountDisplay(el, value, { render } = {}) {
        if (!el) return;
        const fmt = typeof render === 'function' ? render : String;
        const next = Number(value);
        if (!Number.isFinite(next)) {
            el.textContent = fmt(value);
            return;
        }
        const prev = Number(el.dataset.countValue);
        el.dataset.countValue = String(next);
        if (Number.isFinite(prev) && prev !== next) {
            animateCount(el, prev, next, { duration: 600, render });
            Effects?.withTempClass?.(el, 'count-pulse', 650);
        } else if (activeCounts.get(el)?.target === next && !activeCounts.get(el).cancelled) {
            /* A repeat of the value a running tick is heading to: the tick ends
               on it. Writing it now let the tick's next frame write its middle
               number back (the deck count read 13 -> 12 -> 13 -> 12 in the
               battle indicators' twin of this helper, 28 September 2026). */
        } else {
            el.textContent = fmt(next);
        }
    }

    /**
     * Radial spark celebration at a target (generalized from the collection's
     * craft burst) — crafting, exchange transactions, conjure picks, forge
     * reveals. CSS-animated sparks, so it needs no WAAPI.
     */
    function burst(target, { count = 10, radiusX = uiPx(38), radiusY = uiPx(28) } = {}) {
        if (reducedMotion()) return;
        const rect = resolveRect(target);
        if (!rect) return;
        const el = spawn('fx-burst');
        el.style.left = `${rect.left + rect.width / 2}px`;
        el.style.top = `${rect.top + rect.height / 2}px`;
        for (let i = 0; i < count; i += 1) {
            const spark = document.createElement('span');
            spark.className = 'fx-spark';
            spark.style.setProperty('--dx', `${Math.cos((i / count) * Math.PI * 2) * radiusX}px`);
            spark.style.setProperty('--dy', `${Math.sin((i / count) * Math.PI * 2) * radiusY}px`);
            el.appendChild(spark);
        }
        scheduleRemoval(el, 800);
    }

    /** Remove every in-flight draw traveller. The hand render calls this
        before rebuilding seats: a clone flying to a seat that is about to be
        destroyed lands nowhere, while the rebuilt card renders fully visible
        beside it — the "partially ghosted" double image. `keep` names the
        cards (instance ids) whose rebuilt seat takes the arrival over, held
        until its traveller lands (cards.js renderPlayerHand): those keep
        flying, so the draw is never cut short and never shown twice. */
    function cancelDrawTravels({ keep = null } = {}) {
        const layer = document.getElementById(LAYER_ID);
        if (!layer) return;
        for (const el of /** @type {HTMLElement[]} */ ([...layer.querySelectorAll('.fx-draw-travel')])) {
            if (keep && el.dataset.fxDrawFor && keep.has(el.dataset.fxDrawFor)) continue;
            el.remove();
        }
    }

    /**
     * Keep a kept traveller aimed at the seat that took its arrival over. The
     * glide is parked at its destination and animated FLIP-style (glidePlay),
     * so a seat that moved in the rebuild (a card left or joined the hand)
     * would have it land beside the card. It is re-parked on the new seat and
     * the move is taken up by the independent `translate` property over the
     * rest of the flight: no jump, no restart. Returns whether a traveller was
     * found.
     *
     * @param {HTMLElement} cardEl the rebuilt seat that took the arrival over
     */
    function followDrawArrival(cardEl) {
        const instanceId = cardEl?.dataset?.instanceId;
        const layer = document.getElementById(LAYER_ID);
        if (!instanceId || !layer) return false;
        const traveller = /** @type {HTMLElement[]} */ ([...layer.querySelectorAll('.fx-draw-travel')])
            .find((el) => el.dataset.fxDrawFor === instanceId);
        if (!traveller) return false;
        const to = resolveRect(cardEl);
        if (!to || !(to.width > 0)) return true;
        const dx = to.left - (parseFloat(traveller.style.left) || 0);
        const dy = to.top - (parseFloat(traveller.style.top) || 0);
        if (Math.hypot(dx, dy) < 0.5) return true;
        traveller.style.left = `${to.left}px`;
        traveller.style.top = `${to.top}px`;
        let remaining = 0;
        try {
            const flight = traveller.getAnimations?.()[0];
            const end = Number(flight?.effect?.getComputedTiming?.().endTime);
            const now = Number(flight?.currentTime);
            if (Number.isFinite(end) && Number.isFinite(now)) remaining = Math.max(0, end - now);
        } catch (_) { remaining = 0; }
        if (remaining > 0 && canAnimate(traveller) && !reducedMotion()) {
            try {
                traveller.animate([
                    { translate: `${(-dx).toFixed(1)}px ${(-dy).toFixed(1)}px` },
                    { translate: '0px 0px' },
                ], { duration: remaining, easing: 'ease-out' });
            } catch (_) { /* re-parked: lands on the new seat */ }
        }
        return true;
    }

    /** Remove every held glide — snapshot owners use this for stale arrivals.
        Owner-managed clones are exempt: their caller tears them down. */
    function clearHeldGlides() {
        const layer = document.getElementById(LAYER_ID);
        if (!layer) return;
        for (const el of [...layer.querySelectorAll('[data-fx-persist]:not([data-fx-owned])')]) el.remove();
    }

    /**
     * "Deal in" an ordered list of elements — a one-shot staggered entrance in
     * the order given. The per-element delay is capped so a page of 200 cards
     * doesn't animate for seconds. Reduced motion skips it (they just appear).
     * The elements need not share a parent: the cardback page deals in the art
     * wells of its tiles, whose tile chrome never waited.
     */
    function staggerElements(elements, { cap = 12, step = 28, className = 'fx-stagger-in' } = {}) {
        if (!elements || reducedMotion()) return;
        Array.from(elements).forEach((el, i) => {
            if (!el?.classList) return;
            const delay = Math.min(i, cap - 1) * step;
            el.style.setProperty('--stagger-delay', `${delay}ms`);
            el.classList.add(className);
        });
    }

    /**
     * "Deal in a grid" — give a container's children a one-shot staggered
     * entrance. Reusable across collection, deck builder, pack fans, etc.
     */
    function staggerChildren(container, options) {
        if (!container) return;
        staggerElements(container.children, options);
    }

    function isStellarSummon(card, el) {
        if (card && card.isStellar
            && (card.stellarSummonVideoUrl || card.stellarSummonVideoWebmUrl)) return true;
        return !!(el && el.querySelector && el.querySelector('video.stellar-summon-video'));
    }

    /**
     * Start the summon cue so that its peak lands on a contact `contactInMs`
     * from now. Owned by whichever choreography knows its contact — the
     * board landing below for a card that arrives with a snapshot, the enemy
     * traveler in enemy-replay for a glide — and by nothing else, so every
     * summon sounds exactly once, at the moment it is felt. A contact closer
     * than the peak offset starts the clip at once; a Stellar summon carries
     * its own media audio and gets no generic cue.
     * @returns {ReturnType<typeof setTimeout>|null} the timer, for a caller
     *   whose card may vanish before it fires.
     */
    function cueSummonContact(contactInMs, { card = null, cardElement = null } = {}) {
        if (isStellarSummon(card, cardElement)) return null;
        const lead = Math.max(0, Math.round((Number(contactInMs) || 0) - SUMMON_CUE_PEAK_MS));
        const play = () => {
            try { SoundEffects?.play?.('summon'); } catch (_) { /* a silent cue never breaks a landing */ }
        };
        /* Already late for its peak: start it NOW, in this task. A zero-delay
           timer queues behind whatever the landing set off and was measured
           firing 130ms late on the real backend, leaving the sound behind the
           slam it announces. */
        if (lead === 0) {
            play();
            return null;
        }
        return setTimeout(play, lead);
    }

    /**
     * A summon LANDING. The board is rebuilt on every state update, so the
     * real card simply blinks into existence — there is nothing that reads as
     * arrival. This stages the moment instead:
     *
     *   approach → contact (squash, held) → rebound → settle
     *
     * The card falls IN PERSON, carried by its mover (see `moveCard`), so it
     * is present and hit-testable from the first frame — an earlier version
     * hid it behind a clone for ~620ms and killed hover inspection and the
     * touch tap-latch for the whole landing, which is exactly the "flourish
     * that gates input" the juice bar forbids.
     *
     * Contact kicks the floor (flash at the contact line + shockwave + dust),
     * shoves the flanking cards aside and dips the lane under the weight.
     */
    /**
     * THE DRAG AND THE LANDING ARE ONE MOTION. A summon is played by dragging
     * the card onto the battlefield (card-drag.js), and its clone then hangs
     * over the landing slot until the server seats the card. The landing asks
     * for that clone here: CardDrag hands over the exact box it is painted in
     * and removes it, and the real card starts its fall from that very box in
     * the same frame — no swap, no second copy, no restart from above.
     *
     * `noLanding` tells a held clone that nothing will land (a Stellar summon
     * plays its own cinematic), so it gets out of the cinematic's way.
     */
    function claimHeldSummon(card, { noLanding = false, onReady = null } = {}) {
        const instanceId = card?.instanceId;
        if (instanceId === undefined || instanceId === null) return false;
        const detail = { instanceId: String(instanceId), noLanding, claimed: false, onReady };
        try {
            window.dispatchEvent(new CustomEvent('moonfall:summon-landing', { detail }));
        } catch (_) {
            return false;
        }
        return detail.claimed === true;
    }

    function beginEntrance(card, cardElement) {
        // A Stellar summon owns its own cinematic — never compete with it.
        if (!cardElement || isStellarSummon(card, cardElement)) {
            claimHeldSummon(card, { noLanding: true });
            return;
        }
        /* The held clone answers when it has come to rest over the slot —
           at once if it already has, after its glide otherwise — with the box
           it is painted in. No clone for this card: land the ordinary way. */
        const claimed = claimHeldSummon(card, {
            onReady: (fromRect) => landEntrance(card, fromRect),
        });
        if (!claimed) landEntrance(card, null);
    }

    /** The landing proper, from the held clone's box when there was one. */
    function landEntrance(card, heldFrom) {
        // The card already stands in its slot (its drag let it go there).
        if (heldFrom === 'skip') return;
        const cardElement = findBoardCard(card.instanceId);
        if (!cardElement?.isConnected) return;
        const rect = resolveRect(cardElement);
        if (!rect || !(rect.width > 0) || !(rect.height > 0)) return;

        // Reduced motion: the arrival is still ANNOUNCED — a still rim flash
        // over the card, no travel, no squash, no debris — and still heard,
        // its cue aimed at the flash's brightest frame.
        if (reducedMotion()) {
            const still = spawn('fx-card-entrance');
            placeOver(still, rect);
            runAnimation(still, [
                { opacity: 0 }, { opacity: 0.85, offset: 0.35 }, { opacity: 0 },
            ], { duration: 420, easing: 'linear' }, 760, 'fx-overlay-fallback');
            cueSummonContact(420 * 0.35, { card, cardElement });
            return;
        }

        const contactMs = heldFrom
            ? heldLandingPlan(rect, heldFrom).contactMs
            : SUMMON_LAND_MS * 0.34;
        cueSummonContact(contactMs, { card, cardElement });
        stageArrival(cardElement, rect, { from: heldFrom });
    }

    function playEntrance(card) {
        const cardElement = findBoardCard(card.instanceId);
        if (
            !cardElement
            || cardElement.dataset?.enemyReplayArrival === card.instanceId
            || isStellarSummon(card, cardElement)
        ) {
            claimHeldSummon(card, { noLanding: true });
            return;
        }

        // The authoritative snapshot may arrive before a genuinely cold card's
        // art. Board owns that individual presentation gate; the FX layer only
        // observes it and starts the landing on the live replacement node once
        // settled pixels (or a truthful terminal error well) exist.
        const waitForPresentation = Board?.waitForCardPresentation;
        if (typeof waitForPresentation === 'function') {
            let presentation;
            try {
                presentation = waitForPresentation(card.instanceId);
            } catch (err) {
                console.error('[FX] failed to observe summon presentation', err);
                return;
            }
            // The player's held summon has its stand-in (the clone over the
            // slot, card-drag.js) and its own bounds: it lands as it always has.
            const heldByDrag = Boolean(/** @type {HTMLElement} */ (cardElement).dataset?.dragHeldArrival);
            if (presentation && typeof presentation.then === 'function' && heldByDrag) {
                void Promise.resolve(presentation).then(() => {
                    beginEntrance(card, findBoardCard(card.instanceId));
                }).catch((err) => {
                    console.error('[FX] summon presentation did not settle', err);
                });
                return;
            }
            if (presentation && typeof presentation.then === 'function') {
                /* THE CARD ARRIVES WHEN ITS PICTURE CAN ARRIVE WITH IT (round
                   17, 28 September 2026). Nothing stands in for this card (no
                   traveller, no held drag clone: a card a spell conjures, a
                   scripted summon), and it used to stand in its slot while it
                   waited, text over an empty well, then leap up to fall in.
                   The owner: "for conjuring, for example, yeah, you should just
                   try its best to wait a bit to play the animation smoothly".
                   It waits unseen (`data-fx-arrival-wait`, game-board.css; the
                   board carries the mark onto a rebuilt copy) until the board
                   presents it whole, at most the battle's arrival wait, and the
                   landing starts in the task that shows it.
                   Contract: tests/unit/ui/fxEntranceWaitsForArt.test.js. */
                const instanceId = card.instanceId;
                markArrivalWait(instanceId, true);
                let begun = false;
                let capTimer = null;
                const begin = (land) => {
                    if (begun) return;
                    begun = true;
                    clearTimeout(capTimer);
                    markArrivalWait(instanceId, false);
                    if (land) beginEntrance(card, findBoardCard(instanceId));
                };
                capTimer = setTimeout(() => begin(true), arrivalArtCapMs());
                void Promise.resolve(presentation).then(() => begin(true)).catch((err) => {
                    console.error('[FX] summon presentation did not settle', err);
                    begin(false);
                });
                return;
            }
        }

        beginEntrance(card, cardElement);
    }

    /* The battle's arrival wait (Cards.ARRIVAL_ART_WAIT_CAP_MS), read live. */
    function arrivalArtCapMs() {
        const cap = Number(Cards?.ARRIVAL_ART_WAIT_CAP_MS);
        return Number.isFinite(cap) && cap > 0 ? cap : 2000;
    }

    /** A board card waiting unseen for its landing (see playEntrance). */
    function markArrivalWait(instanceId, waiting) {
        const cardElement = /** @type {HTMLElement | null} */ (findBoardCard(instanceId));
        if (!cardElement?.dataset) return;
        if (waiting) cardElement.dataset.fxArrivalWait = String(instanceId);
        else delete cardElement.dataset.fxArrivalWait;
    }

    /* A LANDING FROM THE PLAYER'S HAND (a claimed drag clone) is three beats:
       the card GLIDES from wherever it was let go to hang over its slot, FALLS
       onto it, and takes the SAME squash, rebound and settle as every landing.
       A fast server claims the clone before its own glide to the slot has
       finished, so the landing must own the travel — squeezing a lateral move
       into the fall's accelerating ease made the card jump the last few frames.
       Both the glide and the fall are measured in milliseconds, not fractions,
       so the settle is identical however far the card travelled. */
    const HOVER_RAISE = 0.2;           // of the card's height, as the drag holds it (card-drag.js HOLD_RAISE)
    const HOVER_SCALE = 1.16;          // card-drag.js HOLD_SCALE
    // A slam, not a drift: short and accelerating from a low hang.
    const HELD_FALL_MS = 120;
    /* The player's OWN dragged summon is the loudest thing on the table at
       that moment: it hits harder and is over sooner than the shared landing
       (a blind critic read the shared weight at desktop size as "faint phases
       that read as mush", 2026-09-25). */
    const HELD_SETTLE_MS = 300;
    const HELD_IMPACT_STRENGTH = 1.4;
    /* The last beat of the fall: the card is all but down and already
       flattening, so the squash builds over two frames rather than one — a
       one-frame drop-and-squash reads to the film oracle (and the eye) as a
       snap, not a slam. */
    const HELD_PRE_CONTACT_MS = 36;
    const GLIDE_EASING = 'cubic-bezier(0.25, 0.8, 0.3, 1)';

    /** A transform that paints the card's centre at `cx, cy` at uniform `scale`
        (or at `scaleX` × `scale` wide when the two differ). */
    function footTransform(rect, cx, cy, scale, scaleX = scale) {
        const s = Number.isFinite(scale) && scale > 0 ? scale : 1;
        const sx = Number.isFinite(scaleX) && scaleX > 0 ? scaleX : s;
        const dx = cx - (rect.left + rect.width / 2);
        const dy = cy - (rect.top + rect.height) + (s * rect.height) / 2;
        return `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px) scale(${sx.toFixed(3)}, ${s.toFixed(3)}) rotate(0deg)`;
    }

    /**
     * The keyframes and timings of a landing that starts where a held drag
     * clone was painted. The mover scales about the card's FOOT (origin
     * 50% 100%), so each pose translates the foot to where a card of that size
     * stands with its centre on the requested point.
     */
    function heldLandingPlan(rect, from) {
        const fromScale = rect.width > 0 ? from.width / rect.width : 1;
        const fromCx = from.left + from.width / 2;
        const fromCy = from.top + from.height / 2;
        const hoverCx = rect.left + rect.width / 2;
        const hoverCy = rect.top + rect.height / 2 - rect.height * HOVER_RAISE;
        const distance = Math.hypot(fromCx - hoverCx, fromCy - hoverCy);
        // Already hanging over the slot: straight into the fall.
        const glideMs = distance < rect.height * 0.12
            ? 0
            : Math.round(Math.min(360, Math.max(150, 150 + distance * 0.35)));
        const total = glideMs + HELD_FALL_MS + HELD_SETTLE_MS;
        const at = (ms) => Math.min(0.999, Math.max(0, ms / total));
        const contact = at(glideMs + HELD_FALL_MS);
        /** @type {Keyframe[]} */
        const frames = [
            { transform: footTransform(rect, fromCx, fromCy, fromScale), easing: glideMs ? GLIDE_EASING : FALL_EASING },
        ];
        if (glideMs) {
            frames.push({
                transform: footTransform(rect, hoverCx, hoverCy, HOVER_SCALE),
                offset: at(glideMs),
                easing: FALL_EASING,
            });
        }
        const seatCy = rect.top + rect.height / 2;
        frames.push({
            transform: footTransform(rect, hoverCx, hoverCy + (seatCy - hoverCy) * 0.85, 1.04, 1.15),
            offset: at(glideMs + HELD_FALL_MS - HELD_PRE_CONTACT_MS),
            easing: 'linear',
        });
        const settle = (fraction) => at(glideMs + HELD_FALL_MS + HELD_SETTLE_MS * fraction);
        frames.push(
            { transform: `translate(0px, ${uiPx(7)}px) scale(1.13, 0.85) rotate(0deg)`, offset: contact, easing: 'linear' },
            { transform: `translate(0px, ${uiPx(6)}px) scale(1.11, 0.87) rotate(0deg)`, offset: settle(0.12), easing: SETTLE_EASING },
            { transform: `translate(0px, ${uiPx(-5)}px) scale(0.95, 1.07) rotate(0.9deg)`, offset: settle(0.39), easing: 'ease-in-out' },
            { transform: `translate(0px, ${uiPx(2)}px) scale(1.03, 0.98) rotate(-0.35deg)`, offset: settle(0.67), easing: 'ease-out' },
            { transform: 'translate(0px, 0px) scale(1, 1) rotate(0deg)' },
        );
        return { frames, duration: total, contactMs: glideMs + HELD_FALL_MS };
    }

    /** The landing itself: the card's own fall, and everything it displaces. */
    function stageArrival(cardElement, rect, { from = null } = {}) {
        /* Opt-in, development-only: record what every board description does
           across this landing, so "the other cards on its board lose the card
           description" can be settled from DOM state on the device that shows
           it. No-op unless ?descTrace=1 / localStorage mfDescTrace=1. */
        Cards?.beginDescriptionTrace?.('summon-landing');
        const drop = Math.max(uiPx(28), rect.height * 0.42);
        const instanceId = cardElement.dataset?.instanceId;
        const plan = from ? heldLandingPlan(rect, from) : null;
        const contactMs = plan ? plan.contactMs : Math.round(SUMMON_LAND_MS * 0.34);
        // Origin at the FOOT: a body planted on the board flattens against it;
        // it does not scale about its own middle.
        const landingMotion = moveCard(cardElement, plan ? plan.frames : [
            // Falling in from above, large because it is still near the eye.
            { transform: `translate(0px, ${(-drop).toFixed(1)}px) scale(1.16, 1.16) rotate(-4deg)`, easing: FALL_EASING },
            { transform: `translate(0px, ${(-drop * 0.12).toFixed(1)}px) scale(1.06, 1.06) rotate(-1.4deg)`, offset: 0.26, easing: FALL_EASING },
            // CONTACT — it flattens against the board...
            { transform: `translate(0px, ${uiPx(7)}px) scale(1.13, 0.85) rotate(0deg)`, offset: 0.34, easing: 'linear' },
            // ...and HOLDS the squash for ~3 frames before recovering.
            { transform: `translate(0px, ${uiPx(6)}px) scale(1.11, 0.87) rotate(0deg)`, offset: 0.42, easing: SETTLE_EASING },
            // Rebound through a stretch, then a small counter-squash.
            { transform: `translate(0px, ${uiPx(-5)}px) scale(0.95, 1.07) rotate(0.9deg)`, offset: 0.6, easing: 'ease-in-out' },
            { transform: `translate(0px, ${uiPx(2)}px) scale(1.03, 0.98) rotate(-0.35deg)`, offset: 0.78, easing: 'ease-out' },
            { transform: 'translate(0px, 0px) scale(1, 1) rotate(0deg)' },
        ], { duration: plan ? plan.duration : SUMMON_LAND_MS, easing: 'linear', origin: '50% 100%' });
        const landingOwner = landingMotion && instanceId
            ? activeMoveControllers.get(instanceId)
            : null;
        holdLandingLight(cardElement, plan ? plan.duration : SUMMON_LAND_MS);

        // Everything the landing displaces, fired on the contact frame.
        setTimeout(() => {
            // Another move on this instance supersedes the landing body and
            // its watcher. The old timeout must not leave a bodyless flash,
            // dust burst, neighbour shove, or lane dip behind.
            if (landingOwner && activeMoveControllers.get(instanceId) !== landingOwner) return;
            const liveCard = instanceId ? findBoardCard(instanceId) : cardElement;
            if (
                !liveCard?.isConnected
                || liveCard.classList.contains('card-summon-art-pending')
            ) return;
            summonLandingImpact(liveCard, { strength: from ? HELD_IMPACT_STRENGTH : 1 });
        }, contactMs);
    }

    /* THE ARRIVING CARD KEEPS ITS LIGHT. A new Character takes the "can't use
       this turn" dim (cards.css: brightness 0.7 and a veil on an opaque face). Applied
       on the frame the card was seated, it greyed the card out at the moment of
       contact, and a blind critic read the slam as "the card goes pale and
       see-through, then comes back" (2026-09-25). The card lands at full light
       and eases into its resting state once it has settled. Only the timing of
       a state the card already has changes; the stylesheet owns the look. */
    const LANDING_LIGHT_EASE_MS = 700;

    function holdLandingLight(cardElement, landingMs) {
        const instanceId = cardElement?.dataset?.instanceId;
        const live = () => (instanceId ? findBoardCard(instanceId) : null) || cardElement;
        const mark = (value) => {
            const el = live();
            if (!el?.dataset) return;
            if (value) el.dataset.fxLanding = value;
            else delete el.dataset.fxLanding;
        };
        mark('arriving');
        setTimeout(() => {
            if (live()?.dataset?.fxLanding !== 'arriving') return;
            mark('settling');
            setTimeout(() => {
                if (live()?.dataset?.fxLanding === 'settling') mark(null);
            }, LANDING_LIGHT_EASE_MS);
        }, Math.max(0, Number(landingMs) || 0));
    }

    /**
     * Everything a summon's landing displaces: contact flash, shockwave,
     * dust, the entrance rim, the neighbour shove and the lane dip. Shared
     * so the ENEMY's traveler lands with the same weight as your own card —
     * the enemy path used to fire a lone impact ring at 0.7 while yours got
     * the whole ceremony, which read as "one of them is barely not broken,
     * and the other one is cool". Presentation only, scaled by strength so
     * the opponent's slam can sit a step below your own without losing the
     * vocabulary.
     */
    // Rules text below this size is the reported casualty of the landing's
    // transform animations. Measured 2.07px on the owner's phone; desktop board
    // cards sit far above it, so this reads false there and the motion stays.
    const MIN_RULES_PX_FOR_MOTION = 3;

    function rulesTextSurvivesMotion(cardElement) {
        const desc = cardElement?.querySelector?.('.card-desc');
        if (!desc) return true;
        const px = parseFloat(getComputedStyle(desc).fontSize);
        return !(px > 0) || px >= MIN_RULES_PX_FOR_MOTION;
    }

    function summonLandingImpact(cardElement, { strength = 1 } = {}) {
        if (!cardElement?.isConnected) return;
        const liveRect = resolveRect(cardElement);
        if (!liveRect || !(liveRect.width > 0) || !(liveRect.height > 0)) return;

        contactFlash(liveRect, { strength: 0.95 * strength });
        shockwave(liveRect, { strength });
        if (strength > 1) landingHalo(liveRect);
        debris(liveRect, {
            count: Math.max(6, Math.round(14 * strength)),
            palette: 'dust',
            origin: 'base',
            spread: 1.15,
        });
        const rim = spawn('fx-card-entrance');
        placeOver(rim, liveRect);
        runAnimation(rim, [
            { transform: 'scale(1.05)', opacity: 0 },
            { transform: 'scale(1)', opacity: 0.95, offset: 0.18 },
            { transform: 'scale(1)', opacity: 0 },
        ], { duration: 380, easing: 'ease-out' }, 760, 'fx-overlay-fallback');
        // THE BOUNCE STANDS DOWN WHERE THE RULES TEXT CANNOT SURVIVE A RE-RASTER.
        //
        // Owner's report, twice: "the summon anim has an effect that makes it
        // more physical which bounces other cards and slots. imo that might be
        // the reason for the description vanish", and the constraint that
        // killed every other theory -- "card art is flawless during summon just
        // fyi. only card description vanishes".
        //
        // `neighborPush` and `boardReaction` are the only transform animations
        // in the landing. Both run on the card's own wrapper and its lane, i.e.
        // ANCESTORS of `.card-desc`, which sits in a `isolation: isolate`
        // stacking context. Measured on his iPhone 13 over USB: the page box is
        // 844x280 at DPR 3 and `.card-desc` computes to font-size 2.072321px /
        // line-height 2.486785px -- roughly 6 DEVICE pixels of glyph. A bitmap
        // art well re-rasters through an animated layer intact; fractional
        // sub-3px serif does not, which is exactly the asymmetry he reports.
        //
        // 151 frames sampled on the device during the window prove the loss is
        // NOT state: effective opacity stayed 1 through the whole ancestor
        // chain, nothing set display:none or visibility:hidden, and the box and
        // text length never moved. The fitter is out too -- 97 chars fit the
        // 28x13 box at that size, so its shrink loop never runs.
        //
        // Desktop is deliberately untouched: "this issue doesnt happen on
        // desktop btw" -- there the same string is not sub-3px, so the motion
        // is kept where it is safe and dropped only where it is not.
        if (rulesTextSurvivesMotion(cardElement)) {
            boardNeighbors(cardElement).forEach(neighborPush);
            boardReaction(cardElement, strength);
        }
    }

    /* The player's OWN dragged summon: a ring of gold light blooms out of the
       slot and lingers past the contact frame, so the slam still reads a few
       frames on (a blind critic sampling at ~12fps saw no impact at all when
       every beat of it was shorter than a frame of film, 2026-09-25). Drawn
       as a ring, clear at its centre, so it never washes over the card. */
    const LANDING_HALO_MS = 520;

    function landingHalo(rect) {
        if (!rect || reducedMotion()) return;
        const el = spawn('fx-landing-halo');
        const size = Math.max(rect.width, rect.height) * 1.5;
        el.style.left = `${rect.left + rect.width / 2}px`;
        el.style.top = `${rect.top + rect.height / 2}px`;
        el.style.width = `${size}px`;
        el.style.height = `${size}px`;
        /* LIT ON THE CONTACT FRAME, from the card's own edge. Faded in from
           nothing it peaked ~65ms after the card touched down, and read as "a
           separate later event, not the card hitting the table" (blind critic,
           2026-09-25). */
        runAnimation(el, [
            { transform: 'translate(-50%, -50%) scale(0.7)', opacity: 0.95 },
            { transform: 'translate(-50%, -50%) scale(1)', opacity: 1, offset: 0.12 },
            { transform: 'translate(-50%, -50%) scale(1.45)', opacity: 0.55, offset: 0.5 },
            { transform: 'translate(-50%, -50%) scale(1.8)', opacity: 0 },
        ], { duration: LANDING_HALO_MS, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)' },
        LANDING_HALO_MS + 300, 'fx-overlay-fallback');
    }

    function playStatusChange(change) {
        // Only ADDED tokens read as a hit; removals/reorders (a buff wearing
        // off) must not pulse a phantom impact ring.
        const tokens = (change.added || []).slice(0, 2);
        if (!tokens.length) return;
        const el = findBoardCard(change.card.instanceId);
        if (!el) return;
        impactAt(el, { strength: 0.8 });
        tokens.forEach((token, index) => {
            const color = StoryFeed?.getHighlightColor?.(token) || '';
            setTimeout(() => statusFloat(el, token, color), index * 220);
        });
    }

    function playBoardEffects(plan) {
        if (!plan) return;
        try {
            const captures = plan.captures || [];
            /* The portrait auras repaint every frame; with software rendering
               they and a death together overran the frame. They hold still
               while it plays (thinking-indicator.js holdStill). */
            if (captures.length > 0 && !reducedMotion()) ThinkingIndicator?.holdStill?.(DEATH_STILL_MS);
            captures.forEach(playGhost);
            // Reuse the pre-existing 'destroy' asset already in Convex rather
            // than a bespoke death sound. Fires once per death moment, not
            // per-card, and identically for both players' creatures.
            if (captures.length > 0) SoundEffects?.play('destroy');
            for (const side of ['player', 'opponent']) {
                (plan[side]?.summons || []).forEach(playEntrance);
                (plan[side]?.statusChanges || []).forEach(playStatusChange);
            }
        } catch (err) {
            console.error('[FX] playBoardEffects failed', err);
        }
    }

    return {
        // pure
        diffBoards,
        planBoardEffects,
        shakeParams,
        numberTier,
        numberTravel,
        actionWeight,
        // environment
        reducedMotion,
        ensureLayer,
        // effects
        damageNumber,
        statusFloat,
        impactAt,
        summonLandingImpact,
        cueSummonContact,
        SUMMON_CUE_PEAK_MS,
        impactReaction,
        drawCard,
        spellStrike,
        spellTouch,
        spellSurge,
        lungeAttack,
        debris,
        shockwave,
        screenShake,
        portraitShake,
        captureRects,
        playBoardEffects,
        glidePlay,
        cancelDrawTravels,
        followDrawArrival,
        clearHeldGlides,
        animateCount,
        setCountDisplay,
        burst,
        staggerChildren,
        staggerElements,
    };
})();

export { FX };
